import { ScopedSearch } from "./ScopedSearch";
import { ArtistFollow, FollowedArtists, IdentityForm, PostTools, SharedPost, useIdentityReady, CircleInvite, FollowedArtistFeed } from "./MeetingFeatures";
import { CampaignJoin, CampaignSummary } from "./CampaignJoin";
import { OrderActionPanel, type OrderPanelAction } from "./OrderActionPanel";
import { BlockedArtists, ReportButton, ReportHistory } from "./FeedbackControls";
import { KiraRealtimeClient } from "@kira/realtime-client";
import { ApiClientError, reportClientError } from "@kira/api-client";
import type {
  Artist,
  CampaignDetails,
  ArtistMessage,
  CartItem,
  Circle,
  Conversation,
  CreateCircleInput,
  CreatePostInput,
  CreateProductInput,
  FanToArtistMessage,
  FanRegistrationInput,
  Order,
  Post,
  Product,
  ShipOrderInput,
  SubscriptionOrder,
  SubscriptionPlan,
  User,
} from "@kira/contracts";
import { Button, EmptyState, Skeleton } from "@kira/ui";
import {
  ArrowLeft,
  ArrowUpRight,
  BadgeCheck,
  Bell,
  BellRing,
  BookHeart,
  Check,
  ChevronDown,
  ChevronRight,
  CircleDollarSign,
  CircleAlert,
  CircleUserRound,
  Clock3,
  CloudOff,
  Download,
  Heart,
  House,
  Image as ImageIcon,
  LayoutGrid,
  Menu,
  MessageCircleMore,
  MoreHorizontal,
  PackageCheck,
  PenLine,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  ShoppingBag,
  ShoppingCart,
  Sparkles,
  Star,
  Store,
  TicketCheck,
  UserRound,
  UsersRound,
  Wifi,
  X,
} from "lucide-react";
import {
  FAN_SESSION_CACHE_KEY,
  SessionChangedError,
  cacheCurrentUser,
  createCircle,
  createPost,
  createProduct,
  createSubscriptionOrder,
  clearFanDataCaches,
  getCart,
  getCirclePreferences,
  getCircles,
  getConversations,
  getFanHome,
  getFanMessageHistory,
  getCachedMe,
  getMe,
  getMessages,
  getNotifications,
  getOrders,
  getSellerOrders,
  getPosts,
  getProducts,
  getProfile,
  getPushConfig,
  getSubscriptionPlans,
  leaveArtistMessage,
  logout,
  mutateCircleMembership,
  mutatePostInteraction,
  mutateSubscription,
  newOperationKey,
  publishCampaignDraft,
  readMessages,
  readNotifications,
  registerFan,
  removePushSubscription,
  savePushSubscription,
  setCirclePreferences,
  shipSellerOrder,
  checkout,
  updateCart,
  updateFanProfile,
  type FanHome,
  type FanProfile,
  type SystemNotification,
} from "./api";
import { MarketV2, type MarketV2SellerProfile } from "./MarketV2";
import {
  AboutKira,
  AccountHub,
  ArtistDirectory,
  CircleArrangeSheet,
  CircleDirectory,
  ProfileContentTabs,
  type AboutKiraAction,
  type AccountHubAction,
  type ArtistDirectoryFilter,
  type CircleArrangeItem,
  type CircleDirectorySort,
  type ProfileContentTab,
} from "./FanPrdExperience";
import { SubscriptionAccess } from "./SubscriptionAccess";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from "react";

type MainTab = "home" | "circles" | "market" | "profile";
type HomeMode = "plaza" | "rendez";
type RankRange = "month" | "week" | "day";
type LoadPhase = "booting" | "ready" | "error" | "logged-out";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface ToastMessage {
  id: number;
  title: string;
  body?: string;
  tone?: "good" | "warn";
}

interface FanMessageHistoryChannel {
  items: FanToArtistMessage[];
  nextCursor: string | null;
  hasMore: boolean;
}

const compactNumber = new Intl.NumberFormat("zh-CN", { notation: "compact", maximumFractionDigits: 1 });
const money = new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY" });
const time = new Intl.DateTimeFormat("zh-CN", { hour: "2-digit", minute: "2-digit" });
const chatDay = new Intl.DateTimeFormat("zh-CN", { month: "long", day: "numeric" });
const notificationTime = new Intl.DateTimeFormat("zh-CN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

function initialRoute(): { tab: MainTab; mode: HomeMode; artistId: string | null } {
  const params = new URLSearchParams(window.location.search);
  const requested = params.get("tab");
  if (requested === "rendez") return { tab: "home", mode: "rendez", artistId: params.get("artistId") };
  if (requested === "circles" || requested === "market" || requested === "profile") {
    return { tab: requested, mode: "plaza", artistId: null };
  }
  return { tab: "home", mode: "plaza", artistId: params.get("artistId") };
}

function mergeMessages(current: ArtistMessage[], incoming: ArtistMessage[]): ArtistMessage[] {
  const map = new Map(current.map((message) => [message.id, message]));
  for (const message of incoming) map.set(message.id, message);
  return [...map.values()].sort((a, b) => a.sequence - b.sequence);
}

function mergeFanMessages(current: FanToArtistMessage[], incoming: FanToArtistMessage[]): FanToArtistMessage[] {
  const map = new Map(current.map((message) => [message.id, message]));
  for (const message of incoming) {
    const existing = map.get(message.id);
    map.set(message.id, existing?.status === "read" && message.status === "unread"
      ? { ...message, status: "read", readAt: existing.readAt ?? message.readAt ?? null }
      : message);
  }
  return [...map.values()].sort((left, right) =>
    left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id),
  );
}

function sortConversationsByActivity(conversations: Conversation[]): Conversation[] {
  return [...conversations].sort((left, right) => {
    const rightActivity = Math.max(
      Date.parse(right.latestMessage?.publishedAt ?? right.latestMessage?.createdAt ?? "") || 0,
      Date.parse(right.latestFanMessage?.createdAt ?? "") || 0,
    );
    const leftActivity = Math.max(
      Date.parse(left.latestMessage?.publishedAt ?? left.latestMessage?.createdAt ?? "") || 0,
      Date.parse(left.latestFanMessage?.createdAt ?? "") || 0,
    );
    return rightActivity - leftActivity;
  });
}

function chatDayLabel(value: string): string {
  const date = new Date(value);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) return "今天";
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "昨天";
  return chatDay.format(date);
}

function initials(name: string) {
  return name.trim().slice(0, 2).toUpperCase() || "KI";
}

function maskedName(name: string): string {
  const characters = [...name.trim()];
  if (characters.length <= 1) return "同担*";
  if (characters.length === 2) return `${characters[0]}*`;
  return `${characters[0]}${"*".repeat(Math.min(3, characters.length - 2))}${characters.at(-1)}`;
}

function isAudioMedia(url: string): boolean {
  try {
    return /\.(?:mp3|m4a|aac|ogg|wav)$/i.test(new URL(url, window.location.href).pathname);
  } catch {
    return false;
  }
}

function isAutomationChatMessage(content: string): boolean {
  return /^E2E(?:\s|$)/i.test(content.trim());
}

function Avatar({ src, name, size = "md" }: { src?: string; name: string; size?: "sm" | "md" | "lg" | "xl" }) {
  return (
    <span className={`avatar avatar--${size}`} aria-label={name}>
      <span>{initials(name)}</span>
      {src && navigator.onLine ? <img src={src} alt="" loading="lazy" onError={(event) => { event.currentTarget.hidden = true; }} /> : null}
    </span>
  );
}

function IconButton({ label, children, onClick, className = "" }: { label: string; children: ReactNode; onClick?: () => void; className?: string }) {
  return <button className={`icon-button ${className}`} type="button" aria-label={label} onClick={onClick}>{children}</button>;
}

function SectionHead({ eyebrow, title, action }: { eyebrow: string; title: string; action?: ReactNode }) {
  return (
    <div className="section-head">
      <div>{eyebrow ? <span>{eyebrow}</span> : null}<h2>{title}</h2></div>
      {action}
    </div>
  );
}

function Modal({ title, eyebrow, children, onClose, wide = false, className = "" }: { title: string; eyebrow: string; children: ReactNode; onClose: () => void; wide?: boolean; className?: string }) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className={`modal-sheet${wide ? " modal-sheet--wide" : ""}${className ? ` ${className}` : ""}`} role="dialog" aria-modal="true" aria-labelledby="modal-title">
        <header className="modal-sheet__head">
          <div><span>{eyebrow}</span><h2 id="modal-title">{title}</h2></div>
          <IconButton label="关闭" onClick={onClose}><X size={20} /></IconButton>
        </header>
        <div className="modal-sheet__body">{children}</div>
      </section>
    </div>
  );
}

function Toasts({ messages }: { messages: ToastMessage[] }) {
  return (
    <div className="toasts" aria-live="polite">
      {messages.map((item) => (
        <div className={`toast${item.tone ? ` toast--${item.tone}` : ""}`} key={item.id}>
          <span>{item.tone === "good" ? <Check size={17} /> : item.tone === "warn" ? <CircleAlert size={17} /> : <Sparkles size={17} />}</span>
          <div><strong>{item.title}</strong>{item.body ? <small>{item.body}</small> : null}</div>
        </div>
      ))}
    </div>
  );
}

function LoadingScreen() {
  return (
    <main className="loading-screen" aria-label="正在打开 KIRA">
      <div className="loading-pass">
        <span className="loading-pass__mark"><img src="/kira-mark.svg" alt="" /></span>
        <div><small>KIRA</small><strong>正在进入 KIRA</strong></div>
        <span className="loading-pass__scan" />
      </div>
    </main>
  );
}

function LoginGate({ onRegister, busy, error }: {
  onRegister: (input: FanRegistrationInput) => Promise<void>;
  busy: boolean;
  error: string;
}) {
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const nicknameInput = useRef<HTMLInputElement>(null);
  const phoneInput = useRef<HTMLInputElement>(null);
  const nicknameError = !displayName.trim()
    ? "请先告诉我们怎么称呼你"
    : displayName.trim().length > 30 ? "昵称最多 30 个字" : "";
  const phoneError = /^\d{11}$/.test(phone) ? "" : "请填写 11 位手机号";

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitted(true);
    if (nicknameError) { nicknameInput.current?.focus(); return; }
    if (phoneError) { phoneInput.current?.focus(); return; }
    if (busy) return;
    void onRegister({ displayName: displayName.trim(), phone });
  };

  return (
    <main className="login-gate" aria-labelledby="fan-login-title" aria-busy={busy}>
      <div className="login-gate__visual" aria-hidden="true">
        <img src="/assets/kira-lineup.png" alt="" />
        <span className="login-gate__visual-note"><b>PRIVATE CHANNEL</b><small>和喜欢的人保持联系</small></span>
      </div>
      <section className="login-gate__content">
        <KiraBrand variant="top" label="粉丝入口" />
        <div className="login-gate__intro">
          <span className="overline">FAN ACCESS · 粉丝入口</span>
          <h1 id="fan-login-title">和喜欢的人，聊在一起。</h1>
          <p>收消息、聊近况，也和同好分享每一次心动。</p>
        </div>
        <form className="login-gate__form" onSubmit={submit} noValidate aria-live="polite">
          <label className="login-gate__field">
            <span><b>昵称</b><small>不可重复 · 最多 30 个字</small></span>
            <input
              name="nickname"
              ref={nicknameInput}
              required
              aria-required="true"
              autoComplete="nickname"
              value={displayName}
              maxLength={30}
              aria-invalid={submitted && Boolean(nicknameError)}
              aria-describedby={submitted && nicknameError ? "nickname-error" : undefined}
              placeholder="怎么称呼你"
              onChange={(event) => { setDisplayName(event.target.value); setSubmitted(false); }}
            />
            {submitted && nicknameError ? <em id="nickname-error" role="alert">{nicknameError}</em> : null}
          </label>
          <label className="login-gate__field">
            <span><b>手机号</b><small>{phone.length}/11 · 仅校验位数</small></span>
            <input
              name="phone"
              ref={phoneInput}
              type="tel"
              required
              aria-required="true"
              inputMode="numeric"
              autoComplete="tel"
              value={phone}
              maxLength={11}
              aria-invalid={submitted && Boolean(phoneError)}
              aria-describedby={submitted && phoneError ? "phone-error" : "phone-privacy"}
              placeholder="11 位手机号"
              onChange={(event) => { setPhone(event.target.value.replace(/\D/g, "").slice(0, 11)); setSubmitted(false); }}
            />
            {submitted && phoneError ? <em id="phone-error" role="alert">{phoneError}</em> : null}
          </label>
          <small className="login-gate__privacy" id="phone-privacy"><ShieldCheck size={14} />手机号只用于识别账号，不会展示给其他用户。</small>
          {error ? <p className="login-gate__error" role="alert"><CircleAlert size={16} />{error}</p> : null}
          <Button className="k-button--accent login-gate__action" type="submit" disabled={busy}>
            {busy ? "正在创建…" : "创建账号并进入"}<ArrowUpRight size={18} />
          </Button>
          <small className="login-gate__returning">提交后会创建独立账号；昵称和手机号都不能与其他账号重复。</small>
        </form>
      </section>
    </main>
  );
}

function KiraBrand({ label, variant = "top" }: { label: string; variant?: "top" | "sidebar" }) {
  return (
    <div className={`kira-brand kira-brand--${variant}`} aria-label={`KIRA · ${label}`}>
      <svg className="kira-brand__mark" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
        <path fill="currentColor" d="M20 2 L23.2 15.4 L34 20 L23.2 24.6 L20 38 L16.8 24.6 L6 20 L16.8 15.4 Z" />
        <circle cx="32.6" cy="8.4" r="2.1" fill="currentColor" opacity=".75" />
      </svg>
      <span className="kira-brand__type">
        <strong>KIRA</strong>
        <small><i aria-hidden="true" />{label}</small>
      </span>
    </div>
  );
}

function TopBar({ tab, user, connected, broadcastUnread, systemUnread, onOpenRendez, onOpenSystemInbox, onOpenDrawer }: { tab: MainTab; user: User; connected: boolean; broadcastUnread: number; systemUnread: number; onOpenRendez: () => void; onOpenSystemInbox: () => void; onOpenDrawer: () => void }) {
  const title = tab === "home" ? "KIRA" : tab === "circles" ? "圈子" : tab === "market" ? "市集" : "我的";
  const contextLabel = tab === "home" ? (connected ? "首页" : "连接中") : title;
  return (
    <header className="topbar">
      <KiraBrand label={contextLabel} />
      <div className="topbar__actions">
        <span className={`connection ${connected ? "is-live" : ""}`}><i />{connected ? "在线" : "连接中"}</span>
        <IconButton label={`邂逅${broadcastUnread ? `，${broadcastUnread} 条未读` : ""}`} onClick={onOpenRendez} className="notification-button">
          <MessageCircleMore size={19} />{broadcastUnread ? <b>{broadcastUnread > 99 ? "99+" : broadcastUnread}</b> : null}
        </IconButton>
        <IconButton label={`系统消息${systemUnread ? `，${systemUnread} 条未读` : ""}`} onClick={onOpenSystemInbox} className="notification-button notification-button--system">
          <Bell size={19} />{systemUnread ? <b>{systemUnread > 99 ? "99+" : systemUnread}</b> : null}
        </IconButton>
        <button className="user-chip" type="button" onClick={onOpenDrawer} aria-label="打开个人菜单">
          <Avatar src={user.avatarUrl} name={user.displayName} size="sm" /><span className={`user-chip__presence${connected ? " is-live" : ""}`} aria-hidden="true" /><Menu size={17} />
        </button>
      </div>
    </header>
  );
}

const navItems: Array<{ id: MainTab; label: string; icon: ReactNode }> = [
  { id: "home", label: "首页", icon: <House size={20} /> },
  { id: "circles", label: "圈子", icon: <UsersRound size={20} /> },
  { id: "market", label: "市集", icon: <ShoppingBag size={20} /> },
  { id: "profile", label: "我的", icon: <CircleUserRound size={20} /> },
];

function MainNav({ tab, onChange }: { tab: MainTab; onChange: (tab: MainTab) => void }) {
  const activeIndex = navItems.findIndex((item) => item.id === tab);
  return (
    <nav className="main-nav" aria-label="主导航" style={{ "--nav-index": activeIndex } as CSSProperties}>
      <KiraBrand variant="sidebar" label="与你喜欢的人保持联系" />
      <span className="main-nav__trail" aria-hidden="true" />
      <span className="main-nav__lens" aria-hidden="true"><i /></span>
      {navItems.map((item) => (
        <button type="button" key={item.id} className={tab === item.id ? "is-active" : ""} aria-current={tab === item.id ? "page" : undefined} onClick={() => onChange(item.id)}>
          {item.icon}<span>{item.label}</span>
        </button>
      ))}
      <div className="main-nav__foot"><MessageCircleMore size={17} /><span>有新消息会及时提醒你</span></div>
    </nav>
  );
}

function ArtistCard({ artist, onOpen, busy }: { artist: Artist; onOpen: (artist: Artist) => void; busy: boolean }) {
  return (
    <article className="artist-card frame-iron">
      <button type="button" className="artist-card__visual" onClick={() => onOpen(artist)} aria-label={`查看 ${artist.stageName} 艺人主页`}>
        {artist.coverUrl || artist.avatarUrl ? <img src={artist.coverUrl || artist.avatarUrl} alt={artist.stageName} loading="lazy" /> : <span>{initials(artist.stageName)}</span>}
        <span className="artist-card__category">{artist.category || "艺人"}</span>
        <i style={{ background: artist.accent || "var(--coral)" }} />
      </button>
      <div className="artist-card__body">
        <div><small>{artist.groupName || "个人艺人"}</small><h3>{artist.stageName}</h3></div>
        <p>{artist.bio || "来这里看看最近的作品和动态。"}</p>
        <footer>
          <ArtistFollow artist={artist} />
          <button type="button" className={artist.isSubscribed ? "is-subscribed" : ""} disabled={busy} onClick={() => onOpen(artist)}>
            {artist.isSubscribed ? <><Check size={15} />已订阅</> : <><Plus size={15} />订阅</>}
          </button>
        </footer>
      </div>
    </article>
  );
}

function HomePage({ home, conversations, mode, rankRange, newEncounterCount, onMode, onRange, onOpenArtist, subscriptionBusy, onChat, onExploreCircle, onOpenArtists, onOpenCircles }: {
  home: FanHome | null;
  conversations: Conversation[];
  mode: HomeMode;
  rankRange: RankRange;
  newEncounterCount: number;
  onMode: (mode: HomeMode) => void;
  onRange: (range: RankRange) => void;
  onOpenArtist: (artist: Artist) => void;
  subscriptionBusy: string | null;
  onChat: (conversation: Conversation) => void;
  onExploreCircle: (circle: Circle) => void;
  onOpenArtists: () => void;
  onOpenCircles: () => void;
}) {
  const ranking = home?.rankings[rankRange] ?? [];
  const featuredNames = (home?.featuredArtists ?? []).slice(0, 3).map((artist) => artist.stageName.split(/\s+/)[0]).join(" · ") || "KIRA 艺人阵容";
  return (
    <div className="page home-page">
      <div className="home-switch" role="tablist" aria-label="首页内容">
        <button role="tab" aria-selected={mode === "plaza"} aria-controls="home-plaza" onClick={() => onMode("plaza")}>广场</button>
        <button role="tab" aria-selected={mode === "rendez"} aria-controls="home-messages" aria-label={newEncounterCount ? `邂逅，新增 ${newEncounterCount} 位艺人` : "邂逅"} onClick={() => onMode("rendez")}>邂逅{newEncounterCount ? <b className="home-switch__new-count">{newEncounterCount > 99 ? "99+" : newEncounterCount}</b> : null}</button>
      </div>

      {mode === "rendez" ? <div id="home-messages"><RendezPage conversations={conversations} onChat={onChat} /></div> : (
        <div id="home-plaza" className="home-plaza kira-gate kira-grain">
          <section className="rose-hero" aria-labelledby="home-feature-title">
            <h1 id="home-feature-title">让每一份喜欢，<br/><em>都被看见。</em></h1>
            <button type="button" onClick={onOpenArtists}>认识 {featuredNames}<ArrowUpRight size={14} /></button>
          </section>

          <section className="content-section featured-artists-section">
            <SectionHead eyebrow="" title="最近入驻" action={<button className="text-action" type="button" onClick={onOpenArtists}>全部艺人<ChevronRight size={16} /></button>} />
            <div className="artist-rail">
              {(home?.newArtists?.length ? home.newArtists : home?.featuredArtists ?? []).map((artist) => <ArtistCard key={artist.id} artist={artist} onOpen={onOpenArtist} busy={subscriptionBusy === artist.id} />)}
              {!home ? [0, 1].map((item) => <Skeleton key={item} className="artist-skeleton" />) : null}
            </div>
          </section>

          <div className="hr-iron" aria-hidden="true" />
          <section className="content-section ranking-section">
            <SectionHead eyebrow="" title="人气榜" action={
              <div className="range-tabs">{(["day", "week", "month"] as const).map((range) => <button type="button" key={range} className={rankRange === range ? "is-active" : ""} onClick={() => onRange(range)}>{range === "day" ? "今天" : range === "week" ? "本周" : "本月"}</button>)}</div>
            } />
            <div className="ranking-list">
              {ranking.slice(0, 5).map((artist, index) => (
                <article key={artist.id} role="button" tabIndex={0} onClick={() => onOpenArtist(artist)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") onOpenArtist(artist); }}>
                  <span className="ranking-list__number roman">{["I", "II", "III", "IV", "V"][index] ?? index + 1}</span>
                  <Avatar src={artist.avatarUrl} name={artist.stageName} />
                  <div><strong>{artist.stageName}</strong><small>{artist.groupName || artist.category}</small></div>
                  <span className="ranking-list__heat"><Heart size={14} />{compactNumber.format(artist.heat)}</span>
                  <span className={artist.heatDelta >= 0 ? "delta-up" : "delta-down"}>{artist.heatDelta >= 0 ? "+" : ""}{artist.heatDelta}</span>
                </article>
              ))}
              {home && ranking.length === 0 ? <EmptyState title="排行榜正在校准" body="艺人活跃数据会在下一次统计窗口更新。" /> : null}
            </div>
          </section>

          <section className="content-section home-circles subscribed-home-circles"><SectionHead eyebrow="" title="已订阅的圈子" action={<button className="text-action" onClick={onOpenCircles}>全部圈子</button>} /><div className="home-circle-grid">{home?.circles.filter(circle => circle.kind === "official" && circle.isFollowed).map(circle => <button key={circle.id} onClick={() => onExploreCircle(circle)}><span className="home-circle-grid__cover" data-fallback={initials(circle.name)}>{circle.coverUrl ? <img src={circle.coverUrl} alt="" onError={e=>{e.currentTarget.style.display="none"}} /> : initials(circle.name)}</span><span className="home-circle-grid__copy"><strong>{circle.name}</strong><small className="official-badge">Official</small></span><ChevronRight size={17}/></button>)}</div>{home && !home.circles.some(circle => circle.kind === "official" && circle.isFollowed) ? <p>还没有订阅圈子。关注明星不会自动开通付费圈子。</p> : null}</section>
          {(home?.circles.length ?? 0) > 0 ? <>
            <section className="content-section home-circles">
              <SectionHead eyebrow="" title="热门圈子" action={<button className="text-action" type="button" onClick={onOpenCircles}>全部圈子<ChevronRight size={16} /></button>} />
              <div className="home-circle-grid">{home?.circles.filter(circle => !circle.isFollowed).sort((a,b) => b.activeScore-a.activeScore).slice(0, 4).map((circle) => <button type="button" key={circle.id} onClick={() => onExploreCircle(circle)}><span className="home-circle-grid__cover" data-fallback={initials(circle.name)}>{circle.coverUrl ? <img src={circle.coverUrl} alt="" onError={e=>{e.currentTarget.style.display="none"}} /> : initials(circle.name)}</span><span className="home-circle-grid__copy"><strong><span>{circle.name}</span>{circle.kind === "official" ? <small className="official-badge">Official</small> : null}</strong><small>{compactNumber.format(circle.memberCount)} 位同好</small></span><ChevronRight size={17} /></button>)}</div>
            </section>
          </> : null}
          <FollowedArtistFeed />
        </div>
      )}
    </div>
  );
}

function RendezPage({ conversations, onChat }: { conversations: Conversation[]; onChat: (conversation: Conversation) => void }) {
  return (
    <section className="rendez-page messages-page">
      <header className="messages-page__head">
        <div>
          <h1>邂逅</h1>
          <p>{conversations.length ? `${conversations.length} 位已订阅艺人` : "订阅喜欢的艺人，接收专属消息"}</p>
        </div>
        <Search size={20} aria-hidden="true" />
      </header>
      <p className="chat-semantics">艺人消息向订阅粉丝统一发送；你的留言仅你和艺人可见，艺人暂不支持单独回复。</p>
      <div className="conversation-list">
        {conversations.map((conversation) => {
          const fanIsLatest = Boolean(
            conversation.latestFanMessage
            && (!conversation.latestMessage || conversation.latestFanMessage.createdAt > (conversation.latestMessage.publishedAt ?? conversation.latestMessage.createdAt)),
          );
          const latestAt = fanIsLatest ? conversation.latestFanMessage?.createdAt : conversation.latestMessage?.publishedAt ?? conversation.latestMessage?.createdAt;
          const preview = fanIsLatest
            ? `你：${conversation.latestFanMessage?.content ?? ""}`
            : conversation.latestMessage?.status === "retracted"
              ? "对方撤回了一条消息"
              : conversation.latestMessage?.content || "还没有消息";
          return <button type="button" key={conversation.artist.id} onClick={() => onChat(conversation)}>
            <span className="conversation-list__avatar"><Avatar src={conversation.artist.avatarUrl} name={conversation.artist.stageName} size="lg" /><i /></span>
            <div>
              <span><strong>{conversation.artist.stageName}</strong><small>{latestAt ? time.format(new Date(latestAt)) : "订阅中"}</small></span>
              <small>{conversation.artist.groupName}</small>
              <p className={fanIsLatest ? "is-private-preview" : undefined}>{preview}</p>
            </div>
            {conversation.unreadCount ? <b>{conversation.unreadCount}</b> : <ChevronRight size={18} />}
          </button>;
        })}
        {conversations.length === 0 ? <EmptyState icon={<MessageCircleMore size={30} />} title="还没有聊天" body="订阅艺人后，专属消息会出现在这里。" /> : null}
      </div>
    </section>
  );
}

type ChatTimelineItem =
  | { kind: "artist"; id: string; createdAt: string; message: ArtistMessage }
  | { kind: "fan"; id: string; createdAt: string; message: FanToArtistMessage };

interface PendingFanNote {
  content: string;
  createdAt: string;
  status: "sending" | "error";
}

function ChatScreen({ conversation, messages, fanMessages, user, connected, loading, hasMoreFanMessages, loadingOlderFanMessages, onClose, onRetry, onLoadOlderFanMessages, onLeaveMessage, onBlock }: {
  onBlock: () => void;
  conversation: Conversation;
  messages: ArtistMessage[];
  fanMessages: FanToArtistMessage[];
  user: User;
  connected: boolean;
  loading: boolean;
  hasMoreFanMessages: boolean;
  loadingOlderFanMessages: boolean;
  onClose: () => void;
  onRetry: () => void;
  onLoadOlderFanMessages: () => Promise<void>;
  onLeaveMessage: (content: string, operationKey: string) => Promise<FanToArtistMessage>;
}) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const nearBottomRef = useRef(true);
  const initialScrollDoneRef = useRef(false);
  const restoringOlderRef = useRef(false);
  const [note, setNote] = useState("");
  const [sendingNote, setSendingNote] = useState(false);
  const [noteStatus, setNoteStatus] = useState<"idle" | "sent" | "error">("idle");
  const [pendingNote, setPendingNote] = useState<PendingFanNote | null>(null);
  const noteAttempt = useRef<{ fingerprint: string; key: string } | null>(null);
  const timeline = useMemo<ChatTimelineItem[]>(() => [
    ...messages.filter((message) => !isAutomationChatMessage(message.content)).map((message): ChatTimelineItem => ({
      kind: "artist",
      id: message.id,
      createdAt: message.publishedAt ?? message.createdAt,
      message,
    })),
    ...fanMessages.filter((message) => !isAutomationChatMessage(message.content)).map((message): ChatTimelineItem => ({ kind: "fan", id: message.id, createdAt: message.createdAt, message })),
  ].sort((left, right) =>
    left.createdAt.localeCompare(right.createdAt)
    || left.kind.localeCompare(right.kind)
    || left.id.localeCompare(right.id),
  ), [fanMessages, messages]);
  useEffect(() => {
    if (loading || restoringOlderRef.current) return;
    if (!initialScrollDoneRef.current || nearBottomRef.current) {
      initialScrollDoneRef.current = true;
      requestAnimationFrame(() => endRef.current?.scrollIntoView({ block: "end" }));
    }
  }, [loading, pendingNote?.status, timeline.length]);

  async function loadOlderFanMessages() {
    const body = bodyRef.current;
    if (!body || restoringOlderRef.current) return;
    restoringOlderRef.current = true;
    const previousHeight = body.scrollHeight;
    const previousTop = body.scrollTop;
    try {
      await onLoadOlderFanMessages();
      requestAnimationFrame(() => {
        const currentBody = bodyRef.current;
        if (currentBody) currentBody.scrollTop = previousTop + currentBody.scrollHeight - previousHeight;
        restoringOlderRef.current = false;
      });
    } catch {
      restoringOlderRef.current = false;
    }
  }

  async function sendCurrentNote() {
    const content = note.trim();
    if (!content || sendingNote) return;
    if (!noteAttempt.current || noteAttempt.current.fingerprint !== content) {
      noteAttempt.current = { fingerprint: content, key: newOperationKey(`artist-note:${conversation.artist.id}`) };
    }
    setSendingNote(true);
    setNoteStatus("idle");
    setPendingNote({ content, createdAt: new Date().toISOString(), status: "sending" });
    try {
      await onLeaveMessage(content, noteAttempt.current.key);
      setNote("");
      setNoteStatus("sent");
      setPendingNote(null);
      noteAttempt.current = null;
    } catch {
      setNoteStatus("error");
      setPendingNote((current) => current ? { ...current, status: "error" } : null);
    } finally {
      setSendingNote(false);
    }
  }

  function submitNote(event: FormEvent) {
    event.preventDefault();
    void sendCurrentNote();
  }

  return (
    <section className="chat-screen" aria-label={`与 ${conversation.artist.stageName} 的聊天`}>
      <header className="chat-screen__head">
        <IconButton label="返回邂逅" onClick={onClose}><ArrowLeft size={21} /></IconButton>
        <Avatar src={conversation.artist.avatarUrl} name={conversation.artist.stageName} size="sm" />
        <div><strong>{conversation.artist.stageName}</strong><small><i className={connected ? "is-live" : ""} />{connected ? "已连接" : "正在连接…"}</small></div><button type="button" className="text-action" onClick={onBlock}>拉黑</button>
      </header>
      <div ref={bodyRef} className="chat-screen__body" role="log" aria-label="聊天记录" aria-live="polite" aria-relevant="additions" onScroll={(event) => {
        const body = event.currentTarget;
        nearBottomRef.current = body.scrollHeight - body.scrollTop - body.clientHeight < 96;
      }}>
        <p className="chat-semantics">艺人统一向订阅粉丝发送消息。你的留言仅你和艺人可见，艺人暂不支持单独回复。</p>
        {hasMoreFanMessages ? <button className="chat-history-more" type="button" disabled={loadingOlderFanMessages} onClick={() => void loadOlderFanMessages()}>{loadingOlderFanMessages ? <><RefreshCw size={14} className="spin" />正在加载</> : "查看更早消息"}</button> : null}
        {loading ? <div className="message-loading"><RefreshCw size={21} className="spin" />正在加载消息</div> : null}
        {!loading ? timeline.map((entry, index) => {
          const previous = timeline[index - 1];
          const showDay = !previous || new Date(previous.createdAt).toDateString() !== new Date(entry.createdAt).toDateString();
          if (entry.kind === "artist") {
            const message = entry.message;
            return <Fragment key={`artist:${message.id}`}>
              {showDay ? <div className="chat-day-divider"><span>{chatDayLabel(entry.createdAt)}</span></div> : null}
              <article className={`broadcast-message${message.status === "retracted" ? " broadcast-message--retracted" : ""}`} data-message-kind="artist">
                <div className="broadcast-message__meta"><Avatar src={message.artistAvatarUrl || conversation.artist.avatarUrl} name={message.artistName || conversation.artist.stageName} size="sm" /><span><strong>{message.artistName || conversation.artist.stageName}</strong></span></div>
                <div className="broadcast-message__bubble">
                  <p>{message.status === "retracted" ? "这条消息已撤回" : message.content}</p>
                  {message.status !== "retracted" && message.mediaUrl ? isAudioMedia(message.mediaUrl)
                    ? <audio controls preload="none" src={message.mediaUrl}>你的浏览器暂不支持音频播放。</audio>
                    : <img src={message.mediaUrl} alt="聊天图片" loading="lazy" /> : null}
                  <footer><span>{time.format(new Date(entry.createdAt))}</span>{message.status === "published" ? <ReportButton targetType="message" targetId={message.id} /> : null}</footer>
                </div>
              </article>
            </Fragment>;
          }
          const message = entry.message;
          return <Fragment key={`fan:${message.id}`}>
            {showDay ? <div className="chat-day-divider"><span>{chatDayLabel(entry.createdAt)}</span></div> : null}
            <article className="fan-message" data-message-kind="fan" data-message-id={message.id}>
              <div className="fan-message__bubble">
                <p>{message.content}</p>
                <footer><span>{time.format(new Date(message.createdAt))}</span><span><Check size={13} />{message.status === "read" ? "明星已查看" : "已发送 · 待查看"}</span></footer>
              </div>
            </article>
          </Fragment>;
        }) : null}
        {pendingNote ? <article className={`fan-message fan-message--${pendingNote.status}`} data-message-kind="fan-pending">
          <div className="fan-message__bubble"><p>{pendingNote.content}</p><footer><span>{time.format(new Date(pendingNote.createdAt))}</span><span>{pendingNote.status === "sending" ? <><Clock3 size={13} />发送中</> : <button type="button" onClick={() => void sendCurrentNote()}><CloudOff size={13} />发送失败 · 重试</button>}</span></footer></div>
        </article> : null}
        {!loading && timeline.length === 0 && !pendingNote ? <EmptyState icon={<MessageCircleMore size={30} />} title="还没有消息" body={`想和 ${conversation.artist.stageName} 说什么，就从这里开始吧。`} action={<Button className="k-button--ghost" onClick={onRetry}>刷新</Button>} /> : null}
        <div ref={endRef} />
      </div>
      <form className="fan-note-composer" onSubmit={submitNote}>
        <div><textarea aria-label={`发消息给 ${conversation.artist.stageName}`} id={`fan-note-${conversation.artist.id}`} rows={1} maxLength={500} value={note} onChange={(event) => { setNote(event.target.value); setNoteStatus("idle"); setPendingNote(null); }} placeholder="发消息…"/><button type="submit" disabled={!note.trim() || sendingNote} aria-label="发送消息"><Send size={18}/></button></div>
        {noteStatus === "error" ? <p className="fan-note-composer__status is-error" role="alert"><CloudOff size={13}/>没有发送成功，点按消息重试</p> : null}
      </form>
    </section>
  );
}

function PostCard({ post, onInteraction, onParticipation, onPublishDraft }: { post: Post; onInteraction?: (post: Post, action: "like" | "unlike" | "save" | "unsave") => void; onParticipation?: (post: Post, action: "join" | "leave") => void; onPublishDraft?: (post: Post) => void }) {
  const [deleted, setDeleted] = useState(false);
  if (deleted) return null;
  const statusLabel = post.status === "draft" ? "草稿" : post.status === "pending" ? "审核中" : post.status === "rejected" ? "审核未通过" : post.status === "hidden" ? "已隐藏" : null;
  const participationLabel = post.status === "pending" ? "审核通过后可参与" : post.status === "draft" ? "草稿不可参与" : post.status === "rejected" ? "企划未通过" : post.status === "hidden" ? "企划已隐藏" : post.isParticipating ? "查看我的参与" : post.campaign?.method === "pickup" ? "预约领物料" : post.campaign?.method === "unlock" ? "我来助力" : "参与企划";
  return (
    <article className={`post-card${post.type === "campaign" ? " post-card--campaign" : ""}`}>
      <header><Avatar src={post.authorAvatarUrl} name={post.authorName} size="sm" /><div><span className="post-author-title"><strong>{post.authorName}</strong>{post.authorCircleRole === "owner" || post.authorCircleRole === "admin" ? <small className="circle-role-badge">{post.authorCircleRole === "owner" ? "圈主" : "管理员"}</small> : null}</span><small>{post.circleName} · {new Date(post.createdAt).toLocaleDateString("zh-CN")}</small></div>{statusLabel ? <span className="review-badge">{statusLabel}</span> : null}{post.status === "published" ? <ReportButton targetType="post" targetId={post.id} /> : null}</header>
      {post.type === "campaign" ? <div className="campaign-label"><Sparkles size={15} />应援企划 · {post.campaign?.activityName || "联合行动"}</div> : null}
      <p>{post.content}</p>
      {post.mediaUrls.length ? <div className={`post-media post-media--${Math.min(post.mediaUrls.length, 3)}`}>{post.mediaUrls.slice(0, 3).map((url, index) => <img key={url} src={url} alt={`帖子图片 ${index + 1}`} loading="lazy" />)}</div> : null}
      {post.campaign?.method === "pickup" ? <dl className="campaign-pickup-details"><div><dt>领取时间</dt><dd>{post.campaign.sessionTime}</dd></div><div><dt>领取地点</dt><dd>{post.campaign.meetingPoint}</dd></div><div><dt>物料</dt><dd>{post.campaign.materials?.map(item=>item.name).join("、")} · 每人限领一份</dd></div></dl> : post.campaign ? <div className="campaign-ticket"><span><small>目标</small><strong>{post.campaign.target}</strong></span><span><small>截止</small><strong>{new Date(post.campaign.deadline).toLocaleDateString("zh-CN")}</strong></span><span><small>方式</small><strong>{post.campaign.method === "merch" ? "物料" : post.campaign.method === "unlock" ? "解锁" : "储蓄"}</strong></span></div> : null}
      {post.status === "draft" && onPublishDraft ? <div className="campaign-draft-actions"><span><PenLine size={15} /><strong>企划草稿</strong><small>确认内容后提交运营审核</small></span><button type="button" onClick={() => onPublishDraft(post)}>提交审核<ArrowUpRight size={15} /></button></div> : null}
      <CampaignSummary post={post} />
      {post.type === "campaign" ? <div className="campaign-participation"><span><UsersRound size={15} />{post.campaignParticipationCount} 人参与</span><button type="button" disabled={post.status !== "published" || !onParticipation} className={post.isParticipating ? "is-participating" : ""} onClick={() => onParticipation?.(post, post.isParticipating ? "leave" : "join")}>{participationLabel}</button></div> : null}
      <footer><button type="button" aria-label={post.isLiked ? "取消点赞" : "点赞"} onClick={() => onInteraction?.(post, post.isLiked ? "unlike" : "like")}><Heart size={18} fill={post.isLiked ? "currentColor" : "none"} />{post.likeCount}</button><button type="button" aria-label={post.isSaved ? "取消收藏" : "收藏"} onClick={() => onInteraction?.(post, post.isSaved ? "unsave" : "save")}><BookHeart size={17} fill={post.isSaved ? "currentColor" : "none"} />{post.isSaved ? "已收藏" : "收藏"}</button></footer><PostTools post={post} onDeleted={() => setDeleted(true)} />
    </article>
  );
}

function CirclesPage({ circles, arrangedMyCircles, posts, selectedCircle, sort, loading, membershipBusy, currentUserId, onSelectCircle, onSort, onRefresh, onOpenCircle, onArrange, onMembership, onPostInteraction, onParticipation, onPublishDraft }: {
  circles: Circle[];
  arrangedMyCircles: Circle[];
  posts: Post[];
  selectedCircle: string | null;
  sort: "latest" | "active" | "featured";
  loading: boolean;
  membershipBusy: string | null;
  currentUserId: string;
  onSelectCircle: (id: string | null) => void;
  onSort: (sort: "latest" | "active" | "featured") => void;
  onRefresh: () => void;
  onOpenPost: () => void;
  onOpenCircle: () => void;
  onArrange: () => void;
  onMembership: (circle: Circle, action: "follow" | "unfollow") => void;
  onPostInteraction: (post: Post, action: "like" | "unlike" | "save" | "unsave") => void;
  onParticipation: (post: Post, action: "join" | "leave") => void;
  onPublishDraft: (post: Post) => void;
}) {
  const myCircles = arrangedMyCircles;
  const discoverCircles = circles.filter((circle) => circle.status === "published" && !circle.isFollowed).slice(0, 5);
  const activeCircle = selectedCircle ? circles.find((circle) => circle.id === selectedCircle) : null;
  return (
    <div className="page circles-page">
      <header className="page-title"><div><div><h1>圈子</h1><p>和同好分享近况、收藏与活动。</p></div><span className="page-title__actions"><button type="button" onClick={onArrange}><Settings2 size={16} />调整</button><button type="button" onClick={onOpenCircle}><Plus size={17} />新建</button></span></div></header>
      <div className="circle-rail">
        <button type="button" className={!selectedCircle ? "is-active" : ""} onClick={() => onSelectCircle(null)}><span><LayoutGrid size={18} /></span><strong>全部</strong><small>所有圈子</small></button>
        {myCircles.map((circle) => <button type="button" key={circle.id} className={selectedCircle === circle.id ? "is-active" : ""} onClick={() => onSelectCircle(circle.id)}><span>{circle.coverUrl ? <img src={circle.coverUrl} alt="" /> : initials(circle.name)}</span><strong><span>{circle.name}</span>{circle.kind === "official" ? <small className="official-badge">Official</small> : null}</strong><small>{circle.status === "pending" ? "创建审核中" : circle.status === "rejected" ? "创建未通过" : `${compactNumber.format(circle.memberCount)} 成员`}</small></button>)}
      </div>
      <section className="feed-layout">
        <div className="feed-layout__main">
          <div className="feed-tools circle-feed-tools"><strong><span className="circle-feed-title"><span>{activeCircle?.name ?? "全部圈子"}</span>{activeCircle?.kind === "official" ? <small className="official-badge">Official</small> : null}</span><small className="circle-feed-count">{loading ? "加载中…" : `已加载 ${posts.length} 条动态`}</small></strong><div>{activeCircle ? <CircleInvite key={activeCircle.id} circle={activeCircle} /> : null}{activeCircle?.isFollowed && activeCircle.creatorId !== currentUserId ? <button type="button" disabled={membershipBusy === activeCircle.id} onClick={() => onMembership(activeCircle, "unfollow")}>退出圈子</button> : null}<button type="button" className={sort === "latest" ? "is-active" : ""} onClick={() => onSort("latest")}>最新</button><button type="button" className={sort === "active" ? "is-active" : ""} onClick={() => onSort("active")}>热门</button><button type="button" className={sort === "featured" ? "is-active" : ""} onClick={() => onSort("featured")}>精华</button></div></div>
          {loading ? [0, 1, 2].map((item) => <Skeleton className="post-skeleton" key={item} />) : posts.map((post) => <PostCard post={post} onInteraction={onPostInteraction} onParticipation={onParticipation} onPublishDraft={onPublishDraft} key={post.id} />)}
          {!loading && posts.length === 0 ? <EmptyState icon={<UsersRound size={32} />} title={activeCircle ? `${activeCircle.name}暂无可见动态` : "暂无可见动态"} body="可以发布新动态，或切换到其他圈子看看。" action={<Button className="k-button--ghost" onClick={onRefresh}>重新加载</Button>} /> : null}
        </div>
        <aside className="feed-layout__aside">
          {discoverCircles.length ? <div className="circle-discovery"><header><small>发现圈子</small><strong>先看看，再决定加入</strong></header>{discoverCircles.map((circle) => <article key={circle.id}><button type="button" className="circle-discovery__open" onClick={() => onSelectCircle(circle.id)}><span>{circle.coverUrl ? <img src={circle.coverUrl} alt="" /> : initials(circle.name)}</span><div><strong><span>{circle.name}</span>{circle.kind === "official" ? <small className="official-badge">Official</small> : null}</strong><small>{circle.kind === "official" ? "付费订阅后可加入 · 退出不影响订阅" : `${compactNumber.format(circle.memberCount)} 位同担`}</small></div></button>{circle.kind === "community" || Boolean(circle.artistId) ? <button type="button" className="circle-discovery__follow" disabled={membershipBusy === circle.id} onClick={() => onMembership(circle, "follow")}>{membershipBusy === circle.id ? "加入中" : circle.kind === "official" ? "订阅 / 加入" : "+ 加入"}</button> : null}</article>)}</div> : null}
          <div className="assembly-card"><small>我的圈子</small><strong>{myCircles.length}</strong><span>个已关注圈子</span></div>
        </aside>
      </section>
    </div>
  );
}

type PostComposerField = "circleId" | "content" | "media" | "activityName" | "itemName" | "targetQuantity" | "unlockDescription" | "goalCount" | "fundPurpose" | "goalAmount" | "target" | "deadline" | "coHosts";
type PostComposerErrors = Partial<Record<PostComposerField, string>>;

const postComposerFieldOrder: PostComposerField[] = ["circleId", "content", "media", "activityName", "itemName", "targetQuantity", "unlockDescription", "goalCount", "fundPurpose", "goalAmount", "target", "deadline", "coHosts"];
const postComposerFieldIds: Record<PostComposerField, string> = Object.fromEntries(postComposerFieldOrder.map((field) => [field, `post-composer-${field}`])) as Record<PostComposerField, string>;
const postComposerFieldLabels: Record<PostComposerField, string> = {
  circleId: "发布到",
  content: "正文",
  media: "配图链接",
  activityName: "企划名称",
  itemName: "物料名称",
  targetQuantity: "目标数量",
  unlockDescription: "解锁内容",
  goalCount: "目标人数",
  fundPurpose: "资金用途",
  goalAmount: "目标金额（元）",
  target: "想达成什么",
  deadline: "截止时间",
  coHosts: "联合发起人",
};

function toLocalDateTimeInput(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function defaultCampaignDeadline(): string {
  const date = new Date(Date.now() + 7 * 86_400_000);
  date.setMinutes(Math.ceil(date.getMinutes() / 30) * 30, 0, 0);
  return toLocalDateTimeInput(date);
}

function hasVisibleContent(value: string): boolean {
  return value.replace(/[\s\u200B-\u200D\u2060\uFEFF]/gu, "").length > 0;
}

function parsePostMedia(value: string): { urls: string[]; error?: string } {
  const urls = [...new Set(value.split(/[\n,]/).map((item) => item.trim()).filter(Boolean))];
  if (urls.length > 9) return { urls: [], error: "最多添加 9 张图片链接" };
  const invalidIndex = urls.findIndex((item) => {
    try {
      const parsed = new URL(item);
      return parsed.protocol !== "http:" && parsed.protocol !== "https:";
    } catch {
      return true;
    }
  });
  if (invalidIndex >= 0) return { urls: [], error: `第 ${invalidIndex + 1} 个图片链接不正确` };
  return { urls };
}

function PostComposer({ user, circles, defaultCircleId, onCreated, onClose, notify }: { user: User; circles: Circle[]; defaultCircleId: string | null; onCreated: (post: Post) => void; onClose: () => void; notify: (title: string, body?: string, tone?: ToastMessage["tone"]) => void }) {
  const firstPostableCircleId = circles.find((circle) => circle.canPost)?.id ?? "";
  const initialCircleId = defaultCircleId && circles.some((circle) => circle.id === defaultCircleId && circle.canPost) ? defaultCircleId : firstPostableCircleId;
  const [type, setType] = useState<"normal" | "campaign">("normal");
  const [circleId, setCircleId] = useState(initialCircleId);
  const [content, setContent] = useState("");
  const [media, setMedia] = useState("");
  const [activityName, setActivityName] = useState("");
  const [activityType, setActivityType] = useState<"offline" | "online">("offline");
  const [method, setMethod] = useState<CampaignDetails["method"]>("unlock");
  const [sessionTime, setSessionTime] = useState("");
  const [meetingPoint, setMeetingPoint] = useState("");
  const [pickupNote, setPickupNote] = useState("");
  const [materials, setMaterials] = useState([{ id: "material-1", name: "", spec: "", total: 1 }]);
  const [tiersText, setTiersText] = useState("");
  const [platformUrl, setPlatformUrl] = useState("");
  const [unitPrice, setUnitPrice] = useState("");
  const [target, setTarget] = useState("");
  const [deadline, setDeadline] = useState(defaultCampaignDeadline);
  const [coHosts, setCoHosts] = useState("");
  const [itemName, setItemName] = useState("");
  const [targetQuantity, setTargetQuantity] = useState("");
  const [unlockDescription, setUnlockDescription] = useState("");
  const [goalCount, setGoalCount] = useState("");
  const [fundPurpose, setFundPurpose] = useState("");
  const [goalAmount, setGoalAmount] = useState("");
  const [fieldErrors, setFieldErrors] = useState<PostComposerErrors>({});
  const [busy, setBusy] = useState(false);
  const operationRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const campaignVerified = useIdentityReady(user);
  const minimumDeadline = toLocalDateTimeInput(new Date(Date.now() + 60_000));

  function clearFieldError(field: PostComposerField) {
    setFieldErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  }

  function clearMethodFieldErrors() {
    const retainedFields = ["circleId", "content", "media", "activityName", "target", "deadline", "coHosts"] as const;
    setFieldErrors((current) => {
      const next: PostComposerErrors = {};
      for (const field of retainedFields) {
        const message = current[field];
        if (message) next[field] = message;
      }
      return next;
    });
  }

  function showFieldErrors(errors: PostComposerErrors) {
    setFieldErrors(errors);
    const firstField = postComposerFieldOrder.find((field) => errors[field]);
    if (!firstField) return;
    window.requestAnimationFrame(() => {
      const control = document.getElementById(postComposerFieldIds[firstField]);
      control?.scrollIntoView({ behavior: "smooth", block: "center" });
      control?.focus({ preventScroll: true });
    });
  }

  function fieldState(field: PostComposerField) {
    const message = fieldErrors[field];
    return {
      id: postComposerFieldIds[field],
      "aria-label": postComposerFieldLabels[field],
      "aria-invalid": Boolean(message),
      "aria-describedby": message ? `${postComposerFieldIds[field]}-error` : undefined,
    };
  }

  function fieldError(field: PostComposerField) {
    const message = fieldErrors[field];
    return message ? <span className="form-field-error" id={`${postComposerFieldIds[field]}-error`} role="alert"><CircleAlert size={13} />{message}</span> : null;
  }

  function buildInput(publishMode: "draft" | "publish"): { input?: CreatePostInput; errors: PostComposerErrors } {
    const errors: PostComposerErrors = {};
    const normalizedContent = content.trim();
    const mediaResult = parsePostMedia(media);
    if (!circles.some((circle) => circle.id === circleId && circle.canPost)) errors.circleId = "请选择一个可以发布的圈子";
    if (!hasVisibleContent(normalizedContent)) errors.content = "写一点想和圈友分享的内容";
    if (mediaResult.error) errors.media = mediaResult.error;

    if (type === "normal") {
      if (Object.keys(errors).length) return { errors };
      return { errors, input: { circleId, type, content: normalizedContent, mediaUrls: mediaResult.urls, publishMode } };
    }

    const normalizedActivityName = activityName.trim();
    const normalizedTarget = method === "pickup" ? `领取 ${materials.map(item => item.name.trim()).filter(Boolean).join("、")}`.slice(0,200) : target.trim();
    const normalizedCoHosts = coHosts.trim();
    if (normalizedActivityName.length < 2) errors.activityName = "企划名称至少填写 2 个字";
    if (!normalizedTarget) errors.target = "请写清楚这次想达成什么";
    else if (normalizedTarget.length > 200) errors.target = "目标说明最多 200 个字";
    if (normalizedCoHosts.length > 200) errors.coHosts = "联合发起人最多填写 200 个字";

    const deadlineTimestamp = Date.parse(deadline);
    if (!deadline) errors.deadline = "请选择截止时间";
    else if (!Number.isFinite(deadlineTimestamp)) errors.deadline = "截止时间格式不正确";
    else if (deadlineTimestamp <= Date.now()) errors.deadline = "截止时间需要晚于当前时间";

    const tiers = tiersText.trim() ? tiersText.split("\n").filter(line => line.trim()).map(line => { const [count, ...label] = line.split("|"); return { at: Number(count), label: label.join("|").trim() }; }) : [];
    if (method === "unlock" && (tiers.length > 10 || tiers.some((tier, index) => !Number.isInteger(tier.at) || tier.at < 1 || tier.at > 100000000 || !tier.label || tier.label.length > 100 || (index > 0 && tier.at <= tiers[index - 1]!.at)))) errors.unlockDescription = "阶梯每行填写人数|名称，人数须递增，最多 10 档";
    if (method === "unlock" && platformUrl.trim()) { try { if (!["http:", "https:"].includes(new URL(platformUrl.trim()).protocol)) throw new Error(); } catch { errors.unlockDescription = "活动平台须为 HTTP/HTTPS 链接"; } }
    const unitPriceCents = unitPrice.trim() ? yuanToCents(unitPrice) : null;
    if (["merch", "savings"].includes(method) && unitPrice.trim() && (!unitPriceCents || unitPriceCents > 100000000)) errors.content = "请填写有效的每份金额（不超过 100 万元）";
    let goalAmountCents: number | null = null;
    if (method === "merch") {
      const quantity = Number(targetQuantity);
      if (!itemName.trim()) errors.itemName = "请填写物料名称";
      else if (itemName.trim().length > 80) errors.itemName = "物料名称最多 80 个字";
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 10_000_000) errors.targetQuantity = "目标数量需为 1 到 1000 万的整数";
    } else if (method === "unlock") {
      const count = Number(goalCount);
      if (!unlockDescription.trim()) errors.unlockDescription = "请填写达标后会解锁什么";
      else if (unlockDescription.trim().length > 500) errors.unlockDescription = "解锁内容最多 500 个字";
      if (!Number.isInteger(count) || count < 1 || count > 100_000_000) errors.goalCount = "目标人数需为 1 到 1 亿的整数";
    } else if (method === "pickup") {
      if (!sessionTime.trim() || !meetingPoint.trim()) errors.content = "物料领取须填写场次时间和集合点";
      if (!materials.length || materials.some(item => !item.name.trim() || !Number.isInteger(item.total) || item.total < 1 || item.total > 100000)) errors.content = "请填写每款物料名称和 1～100000 的可领取份数";
    } else {
      goalAmountCents = yuanToCents(goalAmount);
      if (!fundPurpose.trim()) errors.fundPurpose = "请说明这笔应援资金的用途";
      else if (fundPurpose.trim().length > 500) errors.fundPurpose = "资金用途最多 500 个字";
      if (goalAmountCents === null || goalAmountCents < 1 || goalAmountCents > 1_000_000_000) errors.goalAmount = "目标金额需在 0.01 元到 1000 万元之间";
    }

    if (Object.keys(errors).length || !Number.isFinite(deadlineTimestamp)) return { errors };
    return {
      errors,
      input: {
        circleId,
        type,
        content: normalizedContent,
        mediaUrls: mediaResult.urls,
        publishMode,
        campaign: {
          activityName: normalizedActivityName,
          activityType: method === "pickup" ? "offline" : activityType,
          method,
          target: normalizedTarget,
          deadline: new Date(deadlineTimestamp).toISOString(),
          ...(normalizedCoHosts ? { coHosts: normalizedCoHosts } : {}),
          ...(method === "merch" ? { itemName: itemName.trim(), targetQuantity: Number(targetQuantity) } : {}),
          ...(method === "unlock" ? { unlockDescription: unlockDescription.trim(), goalCount: Number(goalCount), ...(tiers.length ? { tiers } : {}), ...(platformUrl.trim() ? { platformUrl: platformUrl.trim() } : {}) } : {}),
          ...(method === "pickup" ? { sessionTime: sessionTime.trim(), meetingPoint: meetingPoint.trim(), pickupNote: pickupNote.trim(), materials: materials.map(item => ({ ...item, name: item.name.trim(), spec: item.spec.trim() })) } : {}),
          ...(["merch", "savings"].includes(method) && unitPriceCents ? { unitPriceCents } : {}),
          ...(method === "savings" && goalAmountCents !== null ? { fundPurpose: fundPurpose.trim(), goalAmountCents } : {}),
        },
      },
    };
  }

  async function submit(event: FormEvent, publishMode: "draft" | "publish" = "publish") {
    event.preventDefault();
    const result = buildInput(publishMode);
    if (!result.input) {
      showFieldErrors(result.errors);
      const firstMessage = postComposerFieldOrder.map((field) => result.errors[field]).find(Boolean);
      notify("还有内容需要修改", firstMessage, "warn");
      return;
    }
    setFieldErrors({});
    setBusy(true);
    try {
      const fingerprint = JSON.stringify(result.input);
      if (!operationRef.current || operationRef.current.fingerprint !== fingerprint) {
        operationRef.current = { fingerprint, key: newOperationKey("post") };
      }
      const post = await createPost(result.input, operationRef.current.key);
      operationRef.current = null;
      onCreated(post);
      notify(publishMode === "draft" ? "草稿已保存" : type === "campaign" ? "应援企划已提交" : "动态已发布", publishMode === "draft" ? "只对自己可见，可从“我的”提交审核" : type === "campaign" ? "审核通过后会出现在圈子里" : "已同步到圈子", "good");
      onClose();
    } catch (error) {
      reportClientError(error, { feature: "post-composer", publishMode, postType: type });
      if (error instanceof ApiClientError && error.code === "CAMPAIGN_DEADLINE_INVALID") {
        const errors = { deadline: "截止时间已经过去，请重新选择" } satisfies PostComposerErrors;
        showFieldErrors(errors);
        notify("截止时间需要修改", errors.deadline, "warn");
      } else {
        notify("发布没有完成", error instanceof ApiClientError ? error.message : "请稍后重试", "warn");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="发布到圈子" eyebrow="圈子动态" onClose={onClose} wide className="post-composer">
      {type === "campaign" && !campaignVerified ? <details className="campaign-identity"><summary>先提交实名和手机资料，才可发布应援企划</summary><p>当前为演示提交，不进行实际身份核验。</p><h3>实名认证</h3><IdentityForm kind="realname"/><h3>手机资料</h3><IdentityForm kind="phone"/></details> : null}
      <form className="form-stack" onSubmit={submit} noValidate>
        <div className="form-segment"><button type="button" className={type === "normal" ? "is-active" : ""} onClick={() => { setType("normal"); setFieldErrors({}); }}><PenLine size={17} />普通帖子</button><button type="button" className={type === "campaign" ? "is-active" : ""} onClick={() => { setType("campaign"); setFieldErrors({}); }}><Sparkles size={17} />应援企划</button></div>
        {type === "campaign" ? <div className="form-note"><ShieldCheck size={20} /><span><strong>{campaignVerified ? "资料已提交" : "提交前需要提交认证资料"}</strong>{campaignVerified ? "企划提交后会进入审核，通过后对圈友可见。" : "展开上方资料入口完成提交；不会进行实际身份核验。"}</span></div> : null}
        <label className={fieldErrors.circleId ? "has-error" : ""}><span>发布到</span><select {...fieldState("circleId")} value={circleId} onChange={(event) => { setCircleId(event.target.value); clearFieldError("circleId"); }}><option value="" disabled>选择圈子</option>{circles.filter((circle) => circle.canPost).map((circle) => <option value={circle.id} key={circle.id}>{circle.name}</option>)}</select>{fieldError("circleId")}</label>
        <label className={fieldErrors.content ? "has-error" : ""}><span>正文</span><div className="form-textarea-control"><textarea {...fieldState("content")} value={content} onChange={(event) => { setContent(event.target.value); clearFieldError("content"); }} placeholder={type === "campaign" ? "说说为什么发起，也告诉大家怎么参与…" : "和圈友分享此刻…"} maxLength={5000} rows={6} /><small>{content.length}/5000</small></div>{fieldError("content")}</label>
        <label className={fieldErrors.media ? "has-error" : ""}><span>配图链接（当前不支持直接上传） <em>选填，最多 9 张</em></span><input {...fieldState("media")} value={media} onChange={(event) => { setMedia(event.target.value); clearFieldError("media"); }} placeholder="每行粘贴一个图片链接" />{fieldError("media")}</label>
        {type === "campaign" ? <div className="campaign-fields">
          <header className="campaign-fields__head"><span>企划信息</span><small>带 * 的内容提交前需要填好</small></header>
          <label className={fieldErrors.activityName ? "has-error" : ""}><span>企划名称 *</span><input {...fieldState("activityName")} value={activityName} onChange={(event) => { setActivityName(event.target.value); clearFieldError("activityName"); }} maxLength={80} placeholder="给大家一眼能看懂的名称" />{fieldError("activityName")}</label>
          <div className="field-grid"><label><span>活动形式</span><select value={method === "pickup" ? "offline" : activityType} disabled={method === "pickup"} onChange={(event) => setActivityType(event.target.value as "offline" | "online")}><option value="offline">线下</option><option value="online">线上</option></select></label><label><span>参与方式</span><select value={method} onChange={(event) => { setMethod(event.target.value as typeof method); clearMethodFieldErrors(); }}><option value="unlock">应援解锁</option><option value="pickup">物料领取</option></select></label></div>
          {method === "merch" ? <div className="field-grid"><label className={fieldErrors.itemName ? "has-error" : ""}><span>物料名称 *</span><input {...fieldState("itemName")} value={itemName} onChange={(event) => { setItemName(event.target.value); clearFieldError("itemName"); }} maxLength={80} placeholder="例：粉丝手幅" />{fieldError("itemName")}</label><label className={fieldErrors.targetQuantity ? "has-error" : ""}><span>目标数量 *</span><input {...fieldState("targetQuantity")} type="number" inputMode="numeric" min={1} max={10_000_000} value={targetQuantity} onChange={(event) => { setTargetQuantity(event.target.value); clearFieldError("targetQuantity"); }} placeholder="300" />{fieldError("targetQuantity")}</label></div> : null}
          {method === "unlock" ? <><label className={fieldErrors.unlockDescription ? "has-error" : ""}><span>解锁内容 *</span><input {...fieldState("unlockDescription")} value={unlockDescription} onChange={(event) => { setUnlockDescription(event.target.value); clearFieldError("unlockDescription"); }} maxLength={500} placeholder="例：达标后公开练习室花絮" />{fieldError("unlockDescription")}</label><label className={fieldErrors.goalCount ? "has-error" : ""}><span>目标人数 *</span><input {...fieldState("goalCount")} type="number" inputMode="numeric" min={1} max={100_000_000} value={goalCount} onChange={(event) => { setGoalCount(event.target.value); clearFieldError("goalCount"); }} placeholder="1000" />{fieldError("goalCount")}</label></> : null}
          {method === "savings" ? <div className="field-grid"><label className={fieldErrors.fundPurpose ? "has-error" : ""}><span>资金用途 *</span><input {...fieldState("fundPurpose")} value={fundPurpose} onChange={(event) => { setFundPurpose(event.target.value); clearFieldError("fundPurpose"); }} maxLength={500} placeholder="例：生日线下大屏" />{fieldError("fundPurpose")}</label><label className={fieldErrors.goalAmount ? "has-error" : ""}><span>目标金额（元）*</span><input {...fieldState("goalAmount")} inputMode="decimal" value={goalAmount} onChange={(event) => { setGoalAmount(event.target.value); clearFieldError("goalAmount"); }} maxLength={10} placeholder="5000.00" />{fieldError("goalAmount")}</label></div> : null}
          {method === "pickup" ? <fieldset className="pickup-editor"><legend>现场场次与物料</legend><label><span>领取时间 *</span><input aria-label="领取时间" maxLength={100} value={sessionTime} onChange={e => setSessionTime(e.target.value)} placeholder="例：9 月 14 日 17:30–19:00" /></label><label><span>领取地点 *</span><input aria-label="领取地点" maxLength={200} value={meetingPoint} onChange={e => setMeetingPoint(e.target.value)} placeholder="例：场馆西门" /></label><label><span>领取说明</span><input aria-label="领取说明" maxLength={300} value={pickupNote} onChange={e => setPickupNote(e.target.value)} /></label>{materials.map((item, index) => <div className="pickup-editor__item" key={item.id}><label><span>物料名称 *</span><input aria-label={`物料 ${index + 1} 名称`} maxLength={80} value={item.name} onChange={e => setMaterials(items => items.map(x => x.id === item.id ? { ...x, name: e.target.value } : x))} /></label><label><span>规格说明</span><input aria-label={`物料 ${index + 1} 规格`} maxLength={200} value={item.spec} onChange={e => setMaterials(items => items.map(x => x.id === item.id ? { ...x, spec: e.target.value } : x))} /></label><label><span>可领取份数 *</span><input type="number" min={1} max={100000} aria-label={`物料 ${index + 1} 份数`} value={item.total} onChange={e => setMaterials(items => items.map(x => x.id === item.id ? { ...x, total: Number(e.target.value) } : x))} /></label><button type="button" disabled={materials.length <= 1} onClick={() => setMaterials(items => items.filter(x => x.id !== item.id))}>删除物料</button></div>)}<button type="button" disabled={materials.length >= 20} onClick={() => setMaterials(items => [...items, { id: `material-${crypto.randomUUID()}`, name: "", spec: "", total: 1 }])}>添加物料</button><p>每人限领一份；只有审核通过后才可预约领取。</p></fieldset> : null}
          {method === "unlock" ? <><label><span>解锁阶梯（选填，每行：人数|名称）</span><textarea aria-label="解锁阶梯" value={tiersText} onChange={e => setTiersText(e.target.value)} placeholder={"1000|灯箱三面\n3000|站厅包柱"} /></label><label><span>活动平台链接（选填）</span><input type="url" aria-label="活动平台链接" value={platformUrl} onChange={e => setPlatformUrl(e.target.value)} /></label></> : null}
          {method === "merch" || method === "savings" ? <label><span>每份金额（元，选填）</span><input aria-label="每份金额" inputMode="decimal" value={unitPrice} onChange={e => setUnitPrice(e.target.value)} /><small>仅展示估算，不在平台内收款。</small></label> : null}
          {method !== "pickup" ? <label className={fieldErrors.target ? "has-error" : ""}><span>想达成什么 *</span><input {...fieldState("target")} value={target} onChange={(event) => { setTarget(event.target.value); clearFieldError("target"); }} maxLength={200} placeholder="例：完成 300 份手幅" />{fieldError("target")}</label> : null}
          <label className={fieldErrors.deadline ? "has-error" : ""}><span>截止时间 * <em>默认 7 天后</em></span><input {...fieldState("deadline")} type="datetime-local" value={deadline} min={minimumDeadline} onChange={(event) => { setDeadline(event.target.value); clearFieldError("deadline"); }} />{fieldError("deadline")}</label>
          <label className={fieldErrors.coHosts ? "has-error" : ""}><span>联合发起人 <em>选填，以真实账号确认结果为准</em></span><input {...fieldState("coHosts")} value={coHosts} onChange={(event) => { setCoHosts(event.target.value); clearFieldError("coHosts"); }} maxLength={200} />{fieldError("coHosts")}</label>
        </div> : null}
        {type === "campaign" ? <p className="post-composer__draft-note">保存草稿不会进入审核，之后可从“我的”提交。</p> : null}
        <div className="form-actions"><Button className="k-button--ghost" type="button" onClick={onClose}>取消</Button>{type === "campaign" ? <Button className="k-button--ghost" type="button" disabled={busy || !campaignVerified} onClick={(event) => void submit(event, "draft")}>保存草稿</Button> : null}<Button className="k-button--accent" type="submit" disabled={busy || (type === "campaign" && !campaignVerified)}>{busy ? "正在提交…" : type === "campaign" ? campaignVerified ? "提交企划" : "先提交实名和手机资料" : "发布动态"}<Send size={17} /></Button></div>
      </form>
    </Modal>
  );
}

function CircleComposer({ user, onCreated, onClose, notify }: { user: User; onCreated: (circle: Circle) => void; onClose: () => void; notify: (title: string, body?: string, tone?: ToastMessage["tone"]) => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<"celebrity" | "influencer" | "cp">("celebrity");
  const [privacy, setPrivacy] = useState<"public" | "closed">("public");
  const [busy, setBusy] = useState(false);
  const operationRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const verified = useIdentityReady(user);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const input: CreateCircleInput = { name: name.trim(), description: description.trim(), category, privacy, coFounderNames: [] };
      const fingerprint = JSON.stringify(input);
      if (!operationRef.current || operationRef.current.fingerprint !== fingerprint) {
        operationRef.current = { fingerprint, key: newOperationKey("circle") };
      }
      const circle = await createCircle(input, operationRef.current.key);
      operationRef.current = null;
      onCreated(circle);
      notify("圈子申请已提交", "运营审核通过后会开放成员入口", "good");
      onClose();
    } catch (error) { notify("圈子创建没有完成", error instanceof Error ? error.message : "请稍后再试", "warn"); }
    finally { setBusy(false); }
  }

  return (
    <Modal title="创建粉丝圈子" eyebrow="创建同好社区" onClose={onClose} wide>
      <form className="form-stack" onSubmit={submit}>
        <div className="form-note"><UsersRound size={20} /><span><strong>创建非官方圈子</strong>审核通过后分享圈子链接，即可邀请同好加入。</span></div>
        <div className="form-note"><ShieldCheck size={20} /><span><strong>发起人双认证</strong>{verified ? "认证资料已提交；演示环境不进行实际核验。" : "展开上方资料入口完成提交；不会进行实际身份核验。"}</span></div>
        <label><span>圈子名称</span><input value={name} onChange={(event) => setName(event.target.value)} minLength={2} maxLength={40} required /></label>
        <label><span>圈子介绍</span><textarea value={description} onChange={(event) => setDescription(event.target.value)} minLength={10} maxLength={500} rows={4} required /><small>{description.length}/500</small></label>
        <div className="field-grid"><label><span>类型</span><select value={category} onChange={(event) => setCategory(event.target.value as typeof category)}><option value="celebrity">明星</option><option value="cp">CP</option></select></label><label><span>加入方式</span><select value={privacy} onChange={(event) => setPrivacy(event.target.value as typeof privacy)}><option value="public">公开加入</option><option value="closed">审核加入</option></select></label></div>
        <div className="form-actions"><Button type="button" className="k-button--ghost" onClick={onClose}>取消</Button><Button type="submit" className="k-button--accent" disabled={busy || !verified}>{busy ? "正在提交…" : verified ? "提交创建申请" : "先提交实名和手机资料"}<ArrowUpRight size={17} /></Button></div>
      </form>
    </Modal>
  );
}

function orderStatusText(status: Order["status"]): string {
  return status === "paid" ? "待发货" : status === "shipping" ? "运输中" : status === "completed" ? "已完成" : status === "refunding" ? "退款处理中" : status === "refunded" ? "已退款" : "待付款";
}

function yuanToCents(value: string): number | null {
  const normalized = value.trim();
  if (!/^\d{1,7}(?:\.\d{1,2})?$/.test(normalized)) return null;
  const [yuan = "0", fraction = ""] = normalized.split(".");
  return Number(yuan) * 100 + Number(fraction.padEnd(2, "0"));
}

function ProductComposer({ kind, initialType = "physical", artists, onSubmit, onClose }: {
  kind: "item" | "carpool";
  initialType?: Product["type"];
  artists: Artist[];
  onSubmit: (input: CreateProductInput, operationKey: string) => Promise<void>;
  onClose: () => void;
}) {
  const [step, setStep] = useState(0);
  const [condition, setCondition] = useState("");
  const [shippingPromise, setShippingPromise] = useState("48 小时内发出");
  const [virtualKind, setVirtualKind] = useState("定制创作");
  const [deliveryMethod, setDeliveryMethod] = useState("链接交付");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [type, setType] = useState<Product["type"]>(initialType);
  const [images, setImages] = useState("");
  const [price, setPrice] = useState("");
  const [shipping, setShipping] = useState("0");
  const [stock, setStock] = useState("1");
  const [artistId, setArtistId] = useState("");
  const [deadline, setDeadline] = useState("");
  const [refundAllowed, setRefundAllowed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const operationRef = useRef<{ fingerprint: string; key: string } | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (step !== 3) { setError("请先完成填写并预览商品，再确认发布。"); return; }
    setError("");
    const imageUrls = [...new Set(images.split(/\r?\n/).map((item) => item.trim()).filter(Boolean))];
    const priceCents = yuanToCents(price);
    const shippingCents = type === "virtual" ? 0 : yuanToCents(shipping);
    const parsedStock = Number(stock);
    if (!imageUrls.length || imageUrls.length > 9) { setError("请填写 1 至 9 张图片网址，每行一张。"); return; }
    if (priceCents === null || shippingCents === null) { setError("价格和运费最多保留两位小数。"); return; }
    if (!Number.isInteger(parsedStock) || parsedStock < 1) { setError("库存至少为 1 件。"); return; }
    if ((kind === "carpool" || type === "virtual") && !deadline) { setError(type === "virtual" ? "虚拟商品需要填写最晚交付日期。" : "发车需要填写截止时间。"); return; }
    const deadlineDate = deadline ? new Date(type === "virtual" ? `${deadline}T23:59:59` : deadline) : null;
    if (deadlineDate && (!Number.isFinite(deadlineDate.getTime()) || deadlineDate.getTime() <= Date.now())) {
      setError("截止时间需要晚于当前时间。");
      return;
    }
    setBusy(true);
    try {
      const input: CreateProductInput = {
        title: title.trim(),
        description: description.trim(),
        type,
        imageUrls,
        priceCents,
        shippingCents,
        stock: parsedStock,
        artistId: artistId || null,
        isCarpool: kind === "carpool",
        deadline: deadlineDate?.toISOString() ?? null,
        refundAllowed,
        listingDetails: type === "virtual" ? { virtualKind, deliveryMethod } : { condition, shippingPromise },
      };
      const fingerprint = JSON.stringify(input);
      if (!operationRef.current || operationRef.current.fingerprint !== fingerprint) {
        operationRef.current = { fingerprint, key: newOperationKey(kind === "carpool" ? "carpool" : "product") };
      }
      await onSubmit(input, operationRef.current.key);
      operationRef.current = null;
      onClose();
    } catch {
      setError("发布失败，请检查内容后重试。");
    } finally {
      setBusy(false);
    }
  }

  function next(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    const fields = event.currentTarget.closest("form")?.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(".product-step:not([hidden]) input, .product-step:not([hidden]) textarea, .product-step:not([hidden]) select");
    if (fields && [...fields].some(field => !field.reportValidity())) return;
    setError(""); setStep(value => value + 1);
  }
  return (
    <form className="form-stack product-composer" onSubmit={submit}>
      <ol className="product-steps">{["图片与标题", type === "virtual" ? "定价与类型" : "定价与运费", type === "virtual" ? "交付与说明" : "发货与说明", "预览发布"].map((label,index) => <li key={label} aria-current={step===index ? "step" : undefined}><b>{index+1}</b>{label}</li>)}</ol>
      <section className="product-step form-stack" hidden={step!==0}>
        <div className="form-segment"><button type="button" className={type === "physical" ? "is-active" : ""} onClick={() => setType("physical")}><PackageCheck size={16}/>实物</button><button type="button" className={type === "virtual" ? "is-active" : ""} onClick={() => setType("virtual")}><Download size={16}/>虚拟商品</button></div>
        <label><span>图片链接 <em>当前仅支持链接，不支持直接上传；每行一个，最多 9 张</em></span><textarea value={images} onChange={event => setImages(event.target.value)} rows={3} required placeholder="https://…/商品展示图.jpg"/></label>
        <label><span>标题</span><input value={title} onChange={event => setTitle(event.target.value)} minLength={2} maxLength={100} required/></label>
        <label><span>关联明星</span><select value={artistId} onChange={event => setArtistId(event.target.value)}><option value="">不关联</option>{artists.map(artist => <option key={artist.id} value={artist.id}>{artist.stageName}</option>)}</select></label>
      </section>
      <section className="product-step form-stack" hidden={step!==1}>
        <div className="field-grid"><label><span>单价（元）</span><input type="number" min="0" max="1000000" step="0.01" value={price} onChange={event => setPrice(event.target.value)} required/></label><label><span>库存</span><input type="number" min={1} max={1000000} value={stock} onChange={event => setStock(event.target.value)} required/></label></div>
        {type === "physical" ? <><label><span>成色（选填）</span><select value={condition} onChange={event => setCondition(event.target.value)}><option value="">不选择</option>{["全新未拆","近全新","9 成新","有瑕疵"].map(value => <option key={value}>{value}</option>)}</select></label><label><span>同店运费（元）</span><input aria-label="同店运费（元）" type="number" min="0" max="10000" step="0.01" value={shipping} onChange={event => setShipping(event.target.value)} required/><small>填 0 表示包邮，同一卖家实物订单按最高运费计一次。</small></label></> : <><label><span>虚拟商品类型</span><select value={virtualKind} onChange={event => setVirtualKind(event.target.value)}>{["定制创作","稿件授权","其他服务"].map(value => <option key={value}>{value}</option>)}</select></label><label><span>交付方式</span><input value={deliveryMethod} onChange={event => setDeliveryMethod(event.target.value)} required maxLength={100}/></label><small>虚拟商品不走物流、不收取运费。不支持账号买卖、代付和门票转卖。</small></>}
      </section>
      <section className="product-step form-stack" hidden={step!==2}>
        {type === "physical" ? <label><span>发货时效</span><select value={shippingPromise} onChange={event => setShippingPromise(event.target.value)}>{["24 小时内发出","48 小时内发出","3 日内发出","周末统一发货"].map(value => <option key={value}>{value}</option>)}</select></label> : null}
        {kind === "carpool" || type === "virtual" ? <label><span>{type === "virtual" ? "最晚交付日期" : "截止时间"}</span><input type={type === "virtual" ? "date" : "datetime-local"} value={deadline} onChange={event => setDeadline(event.target.value)} required/></label> : null}
        <label><span>详细说明</span><textarea value={description} onChange={event => setDescription(event.target.value)} minLength={2} maxLength={2000} rows={4} required placeholder={type === "virtual" ? "服务范围、返稿节奏、修改与售后规则" : "成色、规格、瑕疵与售后规则"}/></label>
        <label className="refund-choice"><input type="checkbox" checked={refundAllowed} onChange={event => setRefundAllowed(event.target.checked)}/><span>允许发货前整单退款</span></label>
      </section>
      {step===3 ? <section className="product-preview"><h3>{title}</h3>{images.trim().split(/\s+/)[0] ? <img src={images.trim().split(/\s+/)[0]} alt="商品预览"/> : null}<p>{description}</p><dl><dt>单价 / 库存</dt><dd>¥{price} / {stock} 件</dd><dt>{type === "virtual" ? "交付方式" : "成色与发货"}</dt><dd>{type === "virtual" ? `${virtualKind} · ${deliveryMethod} · ${deadline} 前交付` : `${condition || "未标注成色"} · ${shippingPromise} · 运费 ¥${shipping}`}</dd>{kind === "carpool" ? <><dt>发车时间</dt><dd>{deadline ? new Date(deadline).toLocaleString("zh-CN", {hour12:false}) : "未填写"}</dd></> : null}<dt>退款规则</dt><dd>{refundAllowed ? "允许发货前整单退款" : "不支持发货前退款"}</dd></dl></section> : null}
      {error ? <p className="form-error" role="alert">{error}</p> : null}
      <div className="form-actions"><Button type="button" className="k-button--ghost" onClick={step ? () => setStep(value => value-1) : onClose}>{step ? "上一步" : "取消"}</Button>{step < 3 ? <Button key="next" type="button" className="k-button--accent" onClick={next}>下一步</Button> : <Button key="publish" type="submit" className="k-button--accent" disabled={busy}>{busy ? "正在发布…" : kind === "carpool" ? "确认发车" : "确认出物"}<ArrowUpRight size={17}/></Button>}</div>
    </form>
  );
}

function SellerOrderCard({ order, onShip, notify }: {
  order: Order;
  onShip: (orderId: string, input: ShipOrderInput, operationKey: string) => Promise<void>;
  notify: (title: string, body?: string, tone?: ToastMessage["tone"]) => void;
}) {
  const [carrier, setCarrier] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const operationRef = useRef<{ fingerprint: string; key: string } | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const input = { carrier, trackingNumber };
      const fingerprint = JSON.stringify(input);
      if (!operationRef.current || operationRef.current.fingerprint !== fingerprint) {
        operationRef.current = { fingerprint, key: newOperationKey(`shipment:${order.id}`) };
      }
      await onShip(order.id, input, operationRef.current.key);
      operationRef.current = null;
    }
    catch { notify("运单没有保存", "请检查承运方和运单号后重试", "warn"); }
    finally { setBusy(false); }
  }
  return (
    <article>
      <header><span>订单编号 {order.id.slice(-8).toUpperCase()}</span><b>{orderStatusText(order.status)}</b></header>
      <small className="order-person">买家：{order.buyerName || "粉丝用户"}</small>
      {order.items.map((item) => <p key={item.productId}>{item.title}<span>× {item.quantity}</span></p>)}
      {order.status === "paid" ? <form className="shipping-form" onSubmit={submit}><input value={carrier} onChange={(event) => setCarrier(event.target.value)} minLength={2} maxLength={40} placeholder="承运方，如顺丰速运" required /><input value={trackingNumber} onChange={(event) => setTrackingNumber(event.target.value)} minLength={4} maxLength={80} pattern="[A-Za-z0-9-]+" placeholder="填写运单号" required /><Button className="k-button--accent" type="submit" disabled={busy}>{busy ? "正在保存…" : "确认发货"}</Button></form> : order.trackingNumber ? <div className="tracking-line"><span>{order.carrier}</span><strong>{order.trackingNumber}</strong></div> : null}
      <footer><small>{new Date(order.createdAt).toLocaleDateString("zh-CN")} · 运费 {money.format(order.shippingCents / 100)}</small><strong>{money.format(order.totalCents / 100)}</strong></footer>
    </article>
  );
}

function ProductCard({ product, owned, onAdd }: { product: Product; owned: boolean; onAdd: (product: Product) => void }) {
  return (
    <article className="product-card">
      <div className="product-card__image">{product.imageUrl ? <img src={product.imageUrl} alt={product.title} loading="lazy" /> : <ShoppingBag size={28} />}<span>{product.type === "virtual" ? "虚拟商品" : product.stock > 0 ? `剩 ${product.stock}` : "已售罄"}</span></div>
      <div className="product-card__body">{product.listingDetails ? <dl className="listing-details">{Object.entries(product.listingDetails).filter(([,value]) => value).map(([key,value]) => <div key={key}><dt>{{condition:"成色",shippingPromise:"发货时效",virtualKind:"虚拟类型",deliveryMethod:"交付方式"}[key]}</dt><dd>{value}</dd></div>)}</dl> : null}<small>{product.sellerName}{product.isCarpool ? " · 拼车" : ""}</small><h3>{product.title}</h3><p>{product.description}</p><footer><strong>{money.format(product.priceCents / 100)}</strong><span>{owned ? "我的商品" : `已售 ${product.sales}`}</span><button type="button" onClick={() => onAdd(product)} disabled={product.stock <= 0 || owned} aria-label={owned ? `自己发布的商品：${product.title}` : `加入购物车：${product.title}`}><Plus size={18} /></button></footer></div>
    </article>
  );
}

type MarketPanel = "none" | "cart" | "orders" | "wish" | "publish-item" | "publish-carpool" | "seller-orders";

function MarketPage({ products, cart, orders, sellerOrders, artists, circles, posts, profile, loading, onAddCart, onCartSelection, onCartQuantity, onCartSelectAll, onCheckout, onCreateProduct, onShipOrder, onCreateWish, notify }: {
  products: Product[];
  cart: CartItem[];
  orders: Order[];
  sellerOrders: Order[];
  artists: Artist[];
  circles: Circle[];
  posts: Post[];
  profile: FanProfile | null;
  loading: boolean;
  onAddCart: (product: Product) => void;
  onCartSelection: (item: CartItem, selected: boolean) => void;
  onCartQuantity: (item: CartItem, quantity: number) => void;
  onCartSelectAll: (items: CartItem[], selected: boolean) => void;
  onCheckout: () => void;
  onCreateProduct: (input: CreateProductInput, operationKey: string) => Promise<void>;
  onShipOrder: (orderId: string, input: ShipOrderInput, operationKey: string) => Promise<void>;
  onCreateWish: (circleId: string, content: string, operationKey: string) => Promise<void>;
  notify: (title: string, body?: string, tone?: ToastMessage["tone"]) => void;
}) {
  const [mode, setMode] = useState<"buy" | "sell">("buy");
  const [panel, setPanel] = useState<MarketPanel>("none");
  const [query, setQuery] = useState("");
  const [wish, setWish] = useState("");
  const [wishCircle, setWishCircle] = useState(circles[0]?.id ?? "");
  const [wishBusy, setWishBusy] = useState(false);
  const wishOperationRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const filtered = products.filter((item) => `${item.title} ${item.description} ${item.sellerName}`.toLowerCase().includes(query.toLowerCase()));
  const ownProducts = products.filter((item) => item.sellerId === profile?.user.id);
  const wishPosts = posts.filter((item) => item.content.startsWith("#需求墙"));
  const selectedCount = cart.filter((item) => item.selected).length;
  const cartGroups = [...cart.reduce((groups, item) => {
    const group = groups.get(item.sellerId) ?? { sellerId: item.sellerId, sellerName: item.sellerName, items: [] as CartItem[] };
    group.items.push(item);
    groups.set(item.sellerId, group);
    return groups;
  }, new Map<string, { sellerId: string; sellerName: string; items: CartItem[] }>()).values()];
  const selectedItems = cart.filter((item) => item.selected);
  const estimatedShipping = [...selectedItems.reduce((shipping, item) => {
    if (item.type === "physical") shipping.set(item.sellerId, Math.max(shipping.get(item.sellerId) ?? 0, item.shippingCents));
    return shipping;
  }, new Map<string, number>()).values()].reduce((sum, value) => sum + value, 0);
  const estimatedTotal = selectedItems.reduce((sum, item) => sum + item.priceCents * item.quantity, 0) + estimatedShipping;
  const allSelected = cart.length > 0 && cart.every((item) => item.selected);

  async function submitWish(event: FormEvent) {
    event.preventDefault();
    if (!wishCircle || !wish.trim()) return;
    setWishBusy(true);
    try {
      const fingerprint = JSON.stringify({ circleId: wishCircle, content: wish.trim() });
      if (!wishOperationRef.current || wishOperationRef.current.fingerprint !== fingerprint) {
        wishOperationRef.current = { fingerprint, key: newOperationKey("wish") };
      }
      await onCreateWish(wishCircle, wish.trim(), wishOperationRef.current.key);
      wishOperationRef.current = null;
      setWish("");
      notify("需求已发布", "圈内卖家现在可以看到它", "good");
    }
    catch (error) { notify("需求没有发出去", error instanceof Error ? error.message : "稍后再试", "warn"); }
    finally { setWishBusy(false); }
  }

  const panelTitle = panel === "cart" ? "购物车" : panel === "orders" ? "我的订单" : panel === "wish" ? "需求墙" : panel === "seller-orders" ? "售出订单" : panel === "publish-carpool" ? "我要发车" : "我要出物";
  return (
    <div className="page market-page">
      <header className="market-title"><div><span>同担好物交换站</span><h1>同担市集</h1><p>只交换好东西，也交换一段追星记忆。</p></div><div className="identity-switch"><button type="button" className={mode === "buy" ? "is-active" : ""} onClick={() => setMode("buy")}><ShoppingBag size={17} />买家</button><button type="button" className={mode === "sell" ? "is-active" : ""} onClick={() => setMode("sell")}><Store size={17} />卖家</button></div></header>
      {mode === "buy" ? <>
        <section className="market-console"><label><Search size={19} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索小卡、手幅、娃衣…" /></label><div><button type="button" onClick={() => setPanel("cart")}><ShoppingCart size={18} /><span>购物车</span>{cart.length ? <b>{cart.length}</b> : null}</button><button type="button" onClick={() => setPanel("orders")}><PackageCheck size={18} /><span>我的订单</span>{orders.length ? <b>{orders.length}</b> : null}</button><button type="button" onClick={() => setPanel("wish")}><Star size={18} /><span>需求墙</span></button></div></section>
        <div className="market-ribbon"><span>同担交易</span><strong>大家正在交换</strong><small>同店实物只收一次运费，虚拟商品免运费。</small></div>
        <section className="product-grid">{loading ? [0, 1, 2, 3].map((item) => <Skeleton className="product-skeleton" key={item} />) : filtered.map((product) => <ProductCard product={product} owned={product.sellerId === profile?.user.id} key={product.id} onAdd={onAddCart} />)}</section>
        {!loading && filtered.length === 0 ? <EmptyState icon={<ShoppingBag size={32} />} title="没有找到这件好物" body="换个关键词，或把它写进需求墙，让卖家来回应。" action={<Button className="k-button--ghost" onClick={() => setPanel("wish")}>去需求墙</Button>} /> : null}
      </> : <section className="seller-dashboard"><header><div><small>我是卖家</small><h2>出售与订单</h2><p>发布闲置，也可以查看待发货订单。</p></div><span><Store size={20} />卖家</span></header><div className="seller-actions"><button type="button" onClick={() => setPanel("publish-item")}><Plus size={18} /><strong>我要出物</strong><small>实物或虚拟商品</small></button><button type="button" onClick={() => setPanel("publish-carpool")}><Radio size={18} /><strong>我要发车</strong><small>到期自动关闭</small></button><button type="button" onClick={() => setPanel("seller-orders")}><PackageCheck size={18} /><strong>售出订单</strong><small>{sellerOrders.filter((order) => order.status === "paid").length} 笔待发货</small></button></div><div className="seller-metrics"><div><small>在售</small><strong>{ownProducts.length}</strong></div><div><small>待发货</small><strong>{sellerOrders.filter((order) => order.status === "paid").length}</strong></div><div><small>累计售出</small><strong>{ownProducts.reduce((sum, item) => sum + item.sales, 0)}</strong></div></div><div className="seller-list">{ownProducts.map((product) => <article key={product.id}><div>{product.imageUrl ? <img src={product.imageUrl} alt="" /> : <ShoppingBag />}</div><span><strong>{product.title}</strong><small>{product.isCarpool ? "拼车 · " : ""}库存 {product.stock} · 已售 {product.sales}</small></span><b>{money.format(product.priceCents / 100)}</b></article>)}{ownProducts.length === 0 ? <EmptyState icon={<Store size={30} />} title="还没有在售商品" body="点击“我要出物”或“我要发车”，确认后即可在市集展示。" /> : null}</div></section>}
      {panel !== "none" ? <Modal title={panelTitle} eyebrow="同担市集" onClose={() => setPanel("none")}>
        {panel === "cart" ? <div className="cart-list">{cart.length ? <button className="cart-select-all" type="button" aria-pressed={allSelected} onClick={() => onCartSelectAll(cart, !allSelected)}><span>{allSelected ? <Check size={14} /> : null}</span>{allSelected ? "取消全选" : "全选购物车"}</button> : null}{cartGroups.map((group) => <section className="cart-shop" key={group.sellerId}><header><Store size={15} /><strong>{group.sellerName}</strong><small>同店实物运费只计一次</small></header>{group.items.map((item, index) => { const groupPhysical = group.items.filter((entry) => entry.selected && entry.type === "physical"); const chargedShipping = item.selected && item.type === "physical" && groupPhysical.findIndex((entry) => entry.id === item.id) === 0 ? Math.max(...groupPhysical.map((entry) => entry.shippingCents), 0) : 0; return <article className={item.selected ? "is-selected" : ""} key={item.id}><button className="cart-select" type="button" aria-label={`${item.selected ? "取消选择" : "选择"}：${item.title}`} aria-pressed={item.selected} onClick={() => onCartSelection(item, !item.selected)}>{item.selected ? <Check size={15} /> : null}</button><div>{item.imageUrl ? <img src={item.imageUrl} alt="" /> : <ShoppingBag />}</div><span><strong>{item.title}</strong><small>{money.format(item.priceCents / 100)} × {item.quantity}</small><span className="cart-quantity"><button type="button" aria-label={`减少 ${item.title} 数量`} onClick={() => onCartQuantity(item, Math.max(0, item.quantity - 1))}>−</button><b>{item.quantity}</b><button type="button" aria-label={`增加 ${item.title} 数量`} onClick={() => onCartQuantity(item, item.quantity + 1)}>+</button><button type="button" onClick={() => onCartQuantity(item, 0)}>移除</button></span></span><b>{item.type === "virtual" ? `到手 ${money.format(item.priceCents * item.quantity / 100)} · 免运费` : chargedShipping ? `到手 ${money.format((item.priceCents * item.quantity + chargedShipping) / 100)} · 含运费` : index > 0 && item.selected ? "运费已计在本店另一件商品" : `商品 ${money.format(item.priceCents * item.quantity / 100)}`}</b></article>; })}</section>)}{cart.length ? <div className="cart-total"><span>已选 {selectedCount} 件 · 结算页会显示最终金额</span><strong>{selectedCount ? `预计 ${money.format(estimatedTotal / 100)}` : "尚未选择"}</strong><Button className="k-button--accent" onClick={onCheckout} disabled={!selectedCount}>结算已选商品<ArrowUpRight size={17} /></Button><small>结算时会再次确认价格和库存；虚拟商品不收运费。</small></div> : <EmptyState title="购物车还是空的" body="看到合适的好物，点一下珊瑚色加号即可加入。" />}</div> : null}
        {panel === "orders" ? <div className="order-list">{orders.map((order) => <article key={order.id}><header><span>订单编号 {order.id.slice(-8).toUpperCase()}</span><b>{orderStatusText(order.status)}</b></header>{order.items.map((item) => <p key={item.productId}>{item.title}<span>× {item.quantity}</span></p>)}{order.trackingNumber ? <div className="tracking-line"><span>{order.carrier}</span><strong>{order.trackingNumber}</strong></div> : null}<footer><small>{new Date(order.createdAt).toLocaleDateString("zh-CN")} · 运费 {money.format(order.shippingCents / 100)}</small><strong>{money.format(order.totalCents / 100)}</strong></footer></article>)}{orders.length === 0 ? <EmptyState title="暂无订单" body="你的每一笔平台交易都会在这里留下可追踪的凭证。" /> : null}</div> : null}
        {panel === "seller-orders" ? <div className="order-list seller-order-list">{sellerOrders.map((order) => <SellerOrderCard order={order} key={order.id} onShip={onShipOrder} notify={notify} />)}{sellerOrders.length === 0 ? <EmptyState title="还没有售出订单" body="商品售出后，待发货订单会出现在这里。" /> : null}</div> : null}
        {panel === "publish-item" || panel === "publish-carpool" ? <ProductComposer kind={panel === "publish-carpool" ? "carpool" : "item"} artists={artists} onSubmit={onCreateProduct} onClose={() => setPanel("none")} /> : null}
        {panel === "wish" ? <div className="wish-wall"><form onSubmit={submitWish}><label><span>发布到圈子</span><select value={wishCircle} onChange={(event) => setWishCircle(event.target.value)} required><option value="" disabled>选择圈子</option>{circles.filter((item) => item.canPost).map((circle) => <option value={circle.id} key={circle.id}>{circle.name}</option>)}</select></label><label><span>想找什么</span><textarea value={wish} onChange={(event) => setWish(event.target.value)} placeholder="例：求福冈场限定银边小卡，可接受轻微瑕疵…" rows={4} required /></label><Button className="k-button--accent" type="submit" disabled={wishBusy || !wishCircle || !wish.trim()}>{wishBusy ? "正在发布…" : "发布真实需求"}<Radio size={17} /></Button><small className="privacy-note">需求墙仅展示掩码昵称，不向卖家提供联系方式。</small></form><div className="wish-list"><small>最新需求</small>{wishPosts.slice(0, 5).map((post) => <article key={post.id}><Avatar name={maskedName(post.authorName)} size="sm" /><p>{post.content.replace(/^#需求墙\s*/, "")}</p><span>{maskedName(post.authorName)} · {post.circleName}</span></article>)}{wishPosts.length === 0 ? <p className="wish-empty">还没有人发布需求。第一条可以由你发出。</p> : null}</div></div> : null}
      </Modal> : null}
    </div>
  );
}

function ProfilePage({ profile, posts, contentTab, onContentTab, onEditProfile, onOpenDrawer, onExploreCircles, onPostInteraction, onParticipation, onPublishDraft }: { profile: FanProfile | null; posts: Post[]; contentTab: ProfileContentTab; onContentTab: (tab: ProfileContentTab) => void; onEditProfile: () => void; onOpenDrawer: () => void; onExploreCircles: () => void; onPostInteraction: (post: Post, action: "like" | "unlike" | "save" | "unsave") => void; onParticipation: (post: Post, action: "join" | "leave") => void; onPublishDraft: (post: Post) => void }) {
  const user = profile?.user;
  const stats = profile?.stats;
  return (
    <div className="page profile-page">
      <section className="profile-pass profile-card">
        <header><span>个人主页</span><div className="profile-pass__actions"><IconButton label="编辑个人资料" onClick={onEditProfile}><PenLine size={19} /></IconButton><IconButton label="打开设置" onClick={onOpenDrawer}><Menu size={21} /></IconButton></div></header>
        <div className="profile-pass__identity"><Avatar {...(user?.avatarUrl ? { src: user.avatarUrl } : {})} name={user?.displayName || "KIRA 用户"} size="xl" /><div><h1>{user?.displayName || "粉丝"}</h1><p>{user?.bio || "还没有填写个人简介"}</p><small>等级 {stats?.level ?? 1}</small></div></div>
      </section>
      <section className="profile-stats"><div><strong>{compactNumber.format(stats?.following ?? 0)}</strong><span>我的关注</span></div><div><strong>{stats?.subscriptions ?? 0}</strong><span>订阅艺人</span></div><div><strong>{stats?.fandomDays ?? 0}</strong><span>入坑天数</span></div></section>
      <section className="profile-badges-compact"><header><span><TicketCheck size={17} />勋章</span><button type="button">全部 {profile?.badges.length ?? 0}<ChevronRight size={15} /></button></header><div>{(profile?.badges.length ? profile.badges : ["初次入场"]).map((badge) => <span key={badge}><Sparkles size={13} />{badge}</span>)}</div></section>
      <ProfileContentTabs tab={contentTab} onTabChange={onContentTab} posts={profile?.posts.length ? profile.posts : posts.filter((post) => post.authorId === user?.id)} likedPosts={profile?.likedPosts ?? []} savedPosts={profile?.savedPosts ?? []} renderPost={(post) => <PostCard post={post} onInteraction={onPostInteraction} onParticipation={onParticipation} onPublishDraft={onPublishDraft} />} emptyActions={{ posts: { label: "去圈子发布", onClick: onExploreCircles } }} />
    </div>
  );
}

function ProfileEditor({ user, onSave }: { user: User; onSave: (input: { displayName: string; bio: string }) => Promise<void> }) {
  const [displayName, setDisplayName] = useState(user.displayName);
  const [bio, setBio] = useState(user.bio ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      await onSave({ displayName: displayName.trim(), bio: bio.trim() });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "个人资料没有保存，请稍后重试。");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="profile-editor" onSubmit={submit}>
      <div className="profile-editor__preview"><Avatar {...(user.avatarUrl ? { src: user.avatarUrl } : {})} name={displayName || user.displayName} size="lg" /><span><small>资料预览</small><strong>{displayName.trim() || "你的昵称"}</strong><p>{bio.trim() || "写一句属于你的追星签名。"}</p></span></div>
      <label><span>昵称</span><input value={displayName} minLength={2} maxLength={30} onChange={(event) => setDisplayName(event.target.value)} placeholder="2—30 个字符" required /><small>{displayName.trim().length}/30</small></label>
      <label><span>个人简介</span><textarea value={bio} maxLength={200} rows={4} onChange={(event) => setBio(event.target.value)} placeholder="喜欢发生时，我刚好在场。" /><small>{bio.length}/200</small></label>
      {error ? <p className="profile-editor__error" role="alert">{error}</p> : null}
      <Button className="k-button--accent" type="submit" disabled={busy || displayName.trim().length < 2}>{busy ? "正在保存…" : "保存个人资料"}<Check size={17} /></Button>
      <p className="privacy-note"><ShieldCheck size={14} />仅编辑公开昵称与简介，不涉及实名或手机号资料。</p>
    </form>
  );
}

function InstallGuide({ isIOS, canPrompt, onInstall, onClose }: { isIOS: boolean; canPrompt: boolean; onInstall: () => void; onClose: () => void }) {
  return (
    <Modal title="安装 KIRA 到手机" eyebrow="更方便地打开" onClose={onClose}>
      <div className="install-guide"><div className="install-guide__icon"><img src="/kira-192.png" alt="KIRA 图标" /><i /></div><p>添加到桌面后，可以像普通 App 一样打开，并及时收到新消息。</p>{canPrompt ? <Button className="k-button--accent" onClick={onInstall}><Download size={18} />立即安装</Button> : isIOS ? <ol><li><span>1</span>在 Safari 底部点击“分享”</li><li><span>2</span>向上滑动，选择“添加到主屏幕”</li><li><span>3</span>确认名称 KIRA，然后点击“添加”</li></ol> : <p className="install-guide__tip">请使用浏览器菜单中的“安装应用”或“添加到主屏幕”。</p>}</div>
    </Modal>
  );
}

function notificationTimestamp(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "刚刚" : notificationTime.format(date);
}

function notificationTargetLabel(notification: SystemNotification): string {
  if (notification.target?.type === "product") return "市集通知";
  if (notification.target?.type === "circle") return "圈子审核";
  if (notification.target?.type === "campaign" || notification.target?.type === "post") return "应援审核";
  return "内容审核";
}

function SystemInbox({ notifications, loading, error, onClose, onRetry, onRead, onReadAll }: { notifications: SystemNotification[]; loading: boolean; error: string; onClose: () => void; onRetry: () => void; onRead: (notificationIds: string[]) => void; onReadAll: () => void }) {
  const unreadCount = notifications.filter((item) => item.status === "unread").length;
  return (
    <Modal title="系统消息" eyebrow="REVIEW SIGNAL" onClose={onClose}>
      <section className="system-inbox">
        <header className="system-inbox__summary">
          <span><strong>{unreadCount ? `${unreadCount} 条审核进度待查看` : "审核进度已同步"}</strong>创建圈子和应援后的每一步都会留在这里。</span>
          <button type="button" disabled={!unreadCount} onClick={onReadAll}>全部已读</button>
        </header>
        {error ? <div className="system-inbox__error" role="status"><span>{error}</span><button type="button" onClick={onRetry}>重新获取</button></div> : null}
        {loading && notifications.length === 0 ? <div className="system-inbox__loading"><RefreshCw className="spin" size={18} />正在同步审核进度</div> : null}
        {!loading && notifications.length === 0 && !error ? <EmptyState icon={<BellRing size={30} />} title="还没有系统消息" body="发起圈子或应援后，提交与审核结果会出现在这里。" /> : null}
        <div className="system-inbox__list">
          {notifications.map((item) => {
            const tone = item.type === "moderation_approved" ? "approved" : item.type === "moderation_rejected" ? "rejected" : "submitted";
            return (
              <button className={`system-notification system-notification--${tone}${item.status === "unread" ? " is-unread" : ""}`} type="button" key={item.id} disabled={item.status === "read"} onClick={() => onRead([item.id])} aria-label={`${item.title}${item.status === "unread" ? "，点击标记已读" : "，已读"}`}>
                <span className="system-notification__stamp">{tone === "approved" ? <TicketCheck size={16} /> : tone === "rejected" ? <X size={16} /> : <Clock3 size={16} />}</span>
                <span className="system-notification__content">
                  <span>{tone === "approved" ? "已通过" : tone === "rejected" ? "需修改" : "审核中"}</span>
                  <h3>{item.title || "审核进度更新"}</h3>
                  <p>{item.body || "审核状态已经更新。"}</p>
                  <footer><time dateTime={item.createdAt}>{notificationTimestamp(item.createdAt)}</time><span>{notificationTargetLabel(item)}</span>{item.status === "unread" ? <b>新</b> : null}</footer>
                </span>
              </button>
            );
          })}
        </div>
      </section>
    </Modal>
  );
}

function AccountDrawer({ user, connected, notificationEnabled, installed, systemUnread, onClose, onInstall, onNotifications, onSystemInbox, onLogout }: { user: User; connected: boolean; notificationEnabled: boolean; installed: boolean; systemUnread: number; onClose: () => void; onInstall: () => void; onNotifications: () => void; onSystemInbox: () => void; onLogout: () => void }) {
  return (
    <div className="drawer-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <aside className="account-drawer" role="dialog" aria-modal="true" aria-label="个人菜单">
        <header><span>账号与设置</span><IconButton label="关闭菜单" onClick={onClose}><X size={20} /></IconButton></header>
        <div className="drawer-user"><Avatar src={user.avatarUrl} name={user.displayName} size="lg" /><div><strong>{user.displayName}</strong><small>KIRA 用户</small></div></div>
        <div className="drawer-status"><span><i className={connected ? "is-live" : ""} />消息连接</span><b>{connected ? "正常" : "正在恢复"}</b></div>
        <nav><button className={`drawer-system-entry${systemUnread ? " has-unread" : ""}`} type="button" onClick={onSystemInbox}><BellRing /><span><strong>系统消息</strong><small>{systemUnread ? `${systemUnread} 条进度待查看` : "圈子与应援进度"}</small></span>{systemUnread ? <b>{systemUnread > 99 ? "99+" : systemUnread}</b> : <ChevronRight />}</button><button type="button" onClick={onNotifications}>{notificationEnabled ? <BellRing /> : <Bell />}<span><strong>新消息通知</strong><small>{notificationEnabled ? "已允许锁屏提醒" : "及时收到喜欢的人的消息"}</small></span><ChevronRight /></button><button type="button" onClick={onInstall}><Download /><span><strong>{installed ? "已添加到桌面" : "添加 KIRA 到桌面"}</strong><small>下次打开更方便</small></span><ChevronRight /></button><button type="button"><ShieldCheck /><span><strong>认证状态</strong><small>{user.realnameVerified ? "实名已通过" : "实名待完成"} · {user.phoneVerified ? "手机已验证" : "手机未验证"}</small></span><ChevronRight /></button><button type="button"><Settings2 /><span><strong>隐私与账号</strong><small>订阅、屏蔽与数据设置</small></span><ChevronRight /></button></nav>
        <button className="drawer-logout" type="button" onClick={onLogout}>退出登录</button>
        <footer>KIRA</footer>
      </aside>
    </div>
  );
}

function urlBase64ToUint8Array(base64String: string) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
}

export function App() {
  const route = useMemo(initialRoute, []);
  const [phase, setPhase] = useState<LoadPhase>("booting");
  const [user, setUser] = useState<User | null>(null);
  const [tab, setTab] = useState<MainTab>(route.tab);
  const [homeMode, setHomeMode] = useState<HomeMode>(route.mode);
  const [home, setHome] = useState<FanHome | null>(null);
  const [rankRange, setRankRange] = useState<RankRange>("month");
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Record<string, ArtistMessage[]>>({});
  const [fanMessageHistory, setFanMessageHistory] = useState<Record<string, FanMessageHistoryChannel>>({});
  const [messageLoading, setMessageLoading] = useState(false);
  const [fanHistoryLoadingOlder, setFanHistoryLoadingOlder] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [circles, setCircles] = useState<Circle[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const feedLoadVersionRef = useRef(0);
  const [circleFilter, setCircleFilter] = useState<string | null>(() => new URLSearchParams(location.search).get("circle"));
  const [postSort, setPostSort] = useState<"latest" | "active" | "featured">("latest");
  const [circleLoading, setCircleLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [sellerOrders, setSellerOrders] = useState<Order[]>([]);
  const [marketEntry, setMarketEntry] = useState<"home" | "buyer-orders">("home");
  const [marketLoading, setMarketLoading] = useState(false);
  const [marketProductType, setMarketProductType] = useState<Product["type"]>("physical");
  const [marketComposerKind, setMarketComposerKind] = useState<"item" | "carpool" | null>(null);
  const [campaignId, setCampaignId] = useState<string | null>(null);
  const [orderAction, setOrderAction] = useState<{ order: Order; action: OrderPanelAction; userId: string } | null>(null);
  const [notificationBusy, setNotificationBusy] = useState(false);
  const [marketShippingOrder, setMarketShippingOrder] = useState<Order | null>(null);
  const [marketProductDetail, setMarketProductDetail] = useState<Product | null>(null);
  const [followedMarketProductIds, setFollowedMarketProductIds] = useState<string[]>([]);
  const [followedMarketSellerIds, setFollowedMarketSellerIds] = useState<string[]>([]);
  useEffect(() => {
    for (const [kind,setter] of [["products",setFollowedMarketProductIds],["sellers",setFollowedMarketSellerIds]] as const) {
      try { const rows = JSON.parse(localStorage.getItem(`kira:market:${user?.id}:followed-${kind}`) ?? "[]"); setter(Array.isArray(rows) ? rows.filter((id: unknown) => typeof id === "string") : []); } catch { setter([]); }
    }
  },[user?.id]);
  const [profile, setProfile] = useState<FanProfile | null>(null);
  const [subscriptionBusy, setSubscriptionBusy] = useState<string | null>(null);
  const [artistAccess, setArtistAccess] = useState<Artist | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [seenRendezArtistIds, setSeenRendezArtistIds] = useState<string[]>([]);
  const [rendezSeenReady, setRendezSeenReady] = useState(false);
  const [circleMembershipBusy, setCircleMembershipBusy] = useState<string | null>(null);
  const [artistDirectoryOpen, setArtistDirectoryOpen] = useState(false);
  const [artistDirectoryFilter, setArtistDirectoryFilter] = useState<ArtistDirectoryFilter>("popular");
  const [circleDirectoryOpen, setCircleDirectoryOpen] = useState(false);
  const [circleDirectorySort, setCircleDirectorySort] = useState<CircleDirectorySort>("popular");
  const [circleArrangeOpen, setCircleArrangeOpen] = useState(false);
  const [circleArrangeItems, setCircleArrangeItems] = useState<CircleArrangeItem[]>([]);
  const [profileContentTab, setProfileContentTab] = useState<ProfileContentTab>("posts");
  const [profileEditOpen, setProfileEditOpen] = useState(false);
  const [accountPanel, setAccountPanel] = useState<AccountHubAction | "about" | null>(null);
  const [aboutDetail, setAboutDetail] = useState<AboutKiraAction | null>(null);
  const [postComposer, setPostComposer] = useState(false);
  const [circleComposer, setCircleComposer] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const [installGuide, setInstallGuide] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => window.matchMedia("(display-mode: standalone)").matches || Boolean((navigator as Navigator & { standalone?: boolean }).standalone));
  const [notificationEnabled, setNotificationEnabled] = useState(() => typeof Notification !== "undefined" && Notification.permission === "granted");
  const [systemNotifications, setSystemNotifications] = useState<SystemNotification[]>([]);
  const [systemInboxOpen, setSystemInboxOpen] = useState(false);
  const [systemInboxLoading, setSystemInboxLoading] = useState(false);
  const [systemInboxError, setSystemInboxError] = useState("");
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [fatalMessage, setFatalMessage] = useState("");
  const activeConversationRef = useRef<Conversation | null>(null);
  const messagesRef = useRef<Record<string, ArtistMessage[]>>({});
  const feedQueryRef = useRef({ circleFilter, postSort });
  feedQueryRef.current = { circleFilter, postSort };
  const checkoutOperationRef = useRef<{ fingerprint: string; key: string } | null>(null);
  const campaignPublishKeysRef = useRef(new Map<string, string>());
  const toastId = useRef(0);
  const sessionEpochRef = useRef(0);
  const activeUserIdRef = useRef<string | null>(null);

  const clearAccountState = useCallback(() => {
    activeConversationRef.current = null;
    messagesRef.current = {};
    setHome(null);
    setConversations([]);
    setActiveConversation(null);
    setMessages({});
    setFanMessageHistory({});
    setMessageLoading(false);
    setFanHistoryLoadingOlder(null);
    setConnected(false);
    setCircles([]);
    setPosts([]);
    setProducts([]);
    setCart([]);
    setOrders([]);
    setSellerOrders([]);
    setProfile(null);
    setSeenRendezArtistIds([]);
    setRendezSeenReady(false);
    setSystemNotifications([]);
    setSystemInboxLoading(false);
    setSystemInboxError("");
    setCircleArrangeItems([]);
    setCircleFilter(null);
    setMarketProductDetail(null);
    setMarketShippingOrder(null);
    setOrderAction(null);
    setCampaignId(null);
    setMarketLoading(false);
    setCircleLoading(false);
    setSystemInboxOpen(false);
    setDrawer(false);
  }, []);

  const activateSession = useCallback((nextUser: User) => {
    if (activeUserIdRef.current !== nextUser.id) {
      sessionEpochRef.current += 1;
      clearAccountState();
    }
    activeUserIdRef.current = nextUser.id;
    setUser(nextUser);
  }, [clearAccountState]);

  const invalidateSession = useCallback(() => {
    sessionEpochRef.current += 1;
    activeUserIdRef.current = null;
    clearAccountState();
    setUser(null);
  }, [clearAccountState]);

  const isCurrentSession = useCallback((epoch: number, userId: string | null) =>
    Boolean(userId) && sessionEpochRef.current === epoch && activeUserIdRef.current === userId, []);

  useEffect(() => { activeConversationRef.current = activeConversation; }, [activeConversation]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  useEffect(() => {
    if (phase !== "ready" || !user || !home) return;
    const storageKey = `kira:rendez-seen:${user.id}`;
    const subscribedIds = home.subscriptions.map((artist) => artist.id);
    const subscribedSet = new Set(subscribedIds);
    let storedIds: string[] | null = null;
    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored) as unknown;
        if (Array.isArray(parsed)) storedIds = parsed.filter((value): value is string => typeof value === "string");
      }
    } catch { storedIds = null; }
    const retainedIds = storedIds === null ? subscribedIds : storedIds.filter((artistId) => subscribedSet.has(artistId));
    const nextIds = tab === "home" && homeMode === "rendez" ? subscribedIds : retainedIds;
    const serialized = JSON.stringify(nextIds);
    try {
      if (localStorage.getItem(storageKey) !== serialized) localStorage.setItem(storageKey, serialized);
    } catch { /* storage can be unavailable in strict/private browser modes */ }
    setSeenRendezArtistIds((current) => JSON.stringify(current) === serialized ? current : nextIds);
    setRendezSeenReady(true);
  }, [home, homeMode, phase, tab, user]);

  useEffect(() => {
    const followed = circles.filter((circle) => circle.isFollowed);
    if (!followed.length) { setCircleArrangeItems([]); return; }
    let cancelled = false;
    void getCirclePreferences().catch(() => {
      try {
        const saved = JSON.parse(localStorage.getItem(`kira:circle-arrangement:${user?.id}`) ?? "{}") as { order?: string[]; hidden?: string[] };
        return (saved.order ?? []).map((circleId, position) => ({ circleId, position, hidden: (saved.hidden ?? []).includes(circleId) }));
      } catch { return []; }
    }).then((preferences) => {
      if (cancelled) return;
      const byId = new Map(followed.map((circle) => [circle.id, circle]));
      const preferenceById = new Map(preferences.map((preference) => [preference.circleId, preference]));
      const orderedIds = [
        ...preferences.toSorted((left, right) => left.position - right.position).map((preference) => preference.circleId).filter((id) => byId.has(id)),
        ...followed.map((circle) => circle.id).filter((id) => !preferenceById.has(id)),
      ];
      setCircleArrangeItems(orderedIds.flatMap((id) => {
        const circle = byId.get(id);
        return circle ? [{ circle, hidden: preferenceById.get(id)?.hidden ?? false }] : [];
      }));
    });
    return () => { cancelled = true; };
  }, [circles]);

  const notify = useCallback((title: string, body?: string, tone?: ToastMessage["tone"]) => {
    const id = ++toastId.current;
    setToasts((current) => [...current.filter((item) => item.title !== title), { id, title, ...(body ? { body } : {}), ...(tone ? { tone } : {}) }].slice(-3));
    window.setTimeout(() => setToasts((current) => current.filter((item) => item.id !== id)), 3600);
  }, []);

  const refreshHome = useCallback(async () => {
    const epoch = sessionEpochRef.current;
    const userId = activeUserIdRef.current;
    const next = await getFanHome();
    if (!isCurrentSession(epoch, userId)) throw new SessionChangedError();
    setHome(next);
    if (next.circles.length) setCircles((current) => current.length ? current : next.circles);
    return next;
  }, [isCurrentSession]);
  const refreshConversations = useCallback(async () => {
    const epoch = sessionEpochRef.current;
    const userId = activeUserIdRef.current;
    const next = await getConversations();
    if (!isCurrentSession(epoch, userId)) throw new SessionChangedError();
    setConversations(next);
    return next;
  }, [isCurrentSession]);
  const refreshFanMessageHistory = useCallback(async (artistId: string) => {
    const epoch = sessionEpochRef.current;
    const userId = activeUserIdRef.current;
    const page = await getFanMessageHistory(artistId, { limit: 50 });
    if (!isCurrentSession(epoch, userId)) throw new SessionChangedError();
    setFanMessageHistory((current) => {
      const channel = current[artistId];
      const preserveLoadedRange = Boolean(channel
        && (channel.hasMore || channel.nextCursor || channel.items.length >= page.items.length));
      return {
        ...current,
        [artistId]: {
          items: mergeFanMessages(channel?.items ?? [], page.items),
          nextCursor: preserveLoadedRange ? channel?.nextCursor ?? null : page.nextCursor,
          hasMore: preserveLoadedRange ? channel?.hasMore ?? false : page.hasMore,
        },
      };
    });
    return page;
  }, [isCurrentSession]);
  const refreshSystemNotifications = useCallback(async () => {
    const epoch = sessionEpochRef.current;
    const userId = activeUserIdRef.current;
    setSystemInboxLoading(true); setSystemInboxError("");
    try {
      const next = await getNotifications();
      if (!isCurrentSession(epoch, userId)) throw new SessionChangedError();
      setSystemNotifications(next);
      return next;
    } catch {
      if (isCurrentSession(epoch, userId)) setSystemInboxError("审核进度暂时没有同步，请稍后重试。");
      return null;
    } finally {
      if (isCurrentSession(epoch, userId)) setSystemInboxLoading(false);
    }
  }, [isCurrentSession]);

  const bootstrap = useCallback(async () => {
    const bootstrapEpoch = sessionEpochRef.current;
    setPhase("booting"); setFatalMessage("");
    try {
      let current: User;
      try { current = await getMe(); }
      catch (error) {
        if (error instanceof SessionChangedError) throw error;
        const cachedUser = !navigator.onLine ? getCachedMe() : null;
        if (cachedUser) current = cachedUser;
        else if (error instanceof ApiClientError && error.status === 401) {
          localStorage.removeItem(FAN_SESSION_CACHE_KEY);
          setPhase("logged-out");
          return;
        } else throw error;
      }
      if (sessionEpochRef.current !== bootstrapEpoch) throw new SessionChangedError();
      activateSession(current);
      const activeEpoch = sessionEpochRef.current;
      const [homeResult, conversationResult] = await Promise.allSettled([refreshHome(), refreshConversations(), refreshSystemNotifications()]);
      if (!isCurrentSession(activeEpoch, current.id)) throw new SessionChangedError();
      if (homeResult.status === "rejected" && conversationResult.status === "rejected") throw homeResult.reason;
      if (homeResult.status === "rejected" || conversationResult.status === "rejected") notify("部分入口正在重连", "可用内容已先为你打开", "warn");
      setPhase("ready");
    } catch (error) {
      if (error instanceof SessionChangedError) { window.location.reload(); return; }
      setFatalMessage(error instanceof Error ? error.message : "无法连接服务"); setPhase("error");
    }
  }, [activateSession, isCurrentSession, notify, refreshConversations, refreshHome, refreshSystemNotifications]);

  useEffect(() => { void bootstrap(); }, [bootstrap]);
  useEffect(() => {
    if (phase !== "ready" || !user) return;
    let active = true;
    const refreshRelations = () => { void getProfile().then(next => { if (active) setProfile(next); }).catch(() => {}); };
    const refreshContent = () => { refreshRelations(); void getPosts(circleFilter ?? undefined, postSort).then(next => { if (active) setPosts(next); }).catch(() => {}); };
    window.addEventListener("kira:follows", refreshRelations);
    window.addEventListener("kira:posts", refreshContent);
    return () => { active = false; window.removeEventListener("kira:follows", refreshRelations); window.removeEventListener("kira:posts", refreshContent); };
  }, [phase, user?.id, circleFilter, postSort]);


  useEffect(() => {
    const onOffline = () => notify("网络已断开", "已打开的内容仍可查看，联网后会自动更新", "warn");
    const onCache = () => notify("正在使用离线快照", "联网后会自动更新", "warn");
    window.addEventListener("offline", onOffline);
    window.addEventListener("kira:cache-fallback", onCache);
    return () => { window.removeEventListener("offline", onOffline); window.removeEventListener("kira:cache-fallback", onCache); };
  }, [notify]);

  useEffect(() => {
    const onSessionStorage = (event: StorageEvent) => {
      if (event.key !== FAN_SESSION_CACHE_KEY) return;
      let nextUserId: string | null = null;
      try { nextUserId = event.newValue ? (JSON.parse(event.newValue) as User).id : null; }
      catch { nextUserId = null; }
      if (nextUserId === activeUserIdRef.current) return;
      invalidateSession();
      if (nextUserId) window.location.reload();
      else setPhase("logged-out");
    };
    window.addEventListener("storage", onSessionStorage);
    return () => window.removeEventListener("storage", onSessionStorage);
  }, [invalidateSession]);

  useEffect(() => {
    if (!user) return;
    const onVisibility = () => {
      if (document.visibilityState !== "visible") return;
      void refreshSystemNotifications();
      const active = activeConversationRef.current;
      if (active) {
        void refreshFanMessageHistory(active.artist.id).catch(() => undefined);
        void refreshConversations().catch(() => undefined);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [refreshConversations, refreshFanMessageHistory, refreshSystemNotifications, user]);

  useEffect(() => {
    const capture = (event: Event) => { event.preventDefault(); setInstallPrompt(event as BeforeInstallPromptEvent); };
    const installedHandler = () => { setInstalled(true); setInstallPrompt(null); notify("KIRA 已安装", "从桌面进入，舞台入口会更近", "good"); };
    window.addEventListener("beforeinstallprompt", capture);
    window.addEventListener("appinstalled", installedHandler);
    return () => { window.removeEventListener("beforeinstallprompt", capture); window.removeEventListener("appinstalled", installedHandler); };
  }, [notify]);

  useEffect(() => {
    if (!user) return;
    const epoch = sessionEpochRef.current;
    const userId = user.id;
    const sessionIsActive = () => isCurrentSession(epoch, userId);
    const realtime = new KiraRealtimeClient();
    let refreshVersion = 0;
    let syncTimer: ReturnType<typeof setTimeout> | null = null;
    let connectedOnce = false;
    const syncEntitlements = async () => {
      const version = ++refreshVersion;
      const feedQuery = feedQueryRef.current;
      try {
        const [nextHome, nextConversations, nextCircles, nextProfile, nextPosts, nextOrders, nextSellerOrders] = await Promise.all([getFanHome(), getConversations(), getCircles({ includeHidden: "true" }), getProfile(), getPosts(feedQuery.circleFilter ?? undefined, feedQuery.postSort), getOrders(), getSellerOrders()]);
        if (!sessionIsActive() || version !== refreshVersion) return;
        setHome(nextHome); setConversations(nextConversations); setCircles(nextCircles); setProfile(nextProfile); setOrders(nextOrders); setSellerOrders(nextSellerOrders);
        if (feedQuery.circleFilter === feedQueryRef.current.circleFilter && feedQuery.postSort === feedQueryRef.current.postSort) setPosts(nextPosts);
        setActiveConversation(current => current && nextConversations.some(item => item.artist.id === current.artist.id) ? current : null);
      } catch { if (sessionIsActive()) notify("订阅状态同步失败", "请联网后刷新重试", "warn"); }
    };
    const scheduleEntitlementsSync = () => {
      if (syncTimer) clearTimeout(syncTimer);
      syncTimer = setTimeout(() => { syncTimer = null; void syncEntitlements(); }, 80);
    };
    const offSubscription = realtime.onSubscriptionUpdated(change => {
      if (!sessionIsActive()) return;
      clearFanDataCaches();
      if (change.status !== "subscribed") {
        setConversations(current => current.filter(item => item.artist.id !== change.artist.id));
        setArtistAccess(current => current?.id === change.artist.id ? null : current);
        setActiveConversation(current => current?.artist.id === change.artist.id ? null : current);
        setMessages(current => { const next = { ...current }; delete next[change.artist.id]; return next; });
        setFanMessageHistory(current => { const next = { ...current }; delete next[change.artist.id]; return next; });
      }
      if (change.status === "blocked") {
        setHome(null); setProfile(null); setCircleFilter(null);
        setCircles(current => current.filter(circle => circle.artistId !== change.artist.id));
        setPosts([]);
      }
      scheduleEntitlementsSync();
    });
    const offStatus = realtime.onStatus((isConnected) => {
      if (!sessionIsActive()) return;
      setConnected(isConnected);
      if (isConnected) {
        if (connectedOnce) scheduleEntitlementsSync();
        else void refreshConversations();
        connectedOnce = true;
        const active = activeConversationRef.current;
        if (active) {
          const existing = messagesRef.current[active.artist.id] ?? [];
          const after = existing.at(-1)?.sequence;
          void getMessages(active.artist.id, after).then((incoming) => {
            if (sessionIsActive()) setMessages((current) => ({ ...current, [active.artist.id]: mergeMessages(current[active.artist.id] ?? [], incoming) }));
          }).catch(() => undefined);
          void refreshFanMessageHistory(active.artist.id).catch(() => undefined);
        }
      }
    });
    const offMessage = realtime.onMessage((message) => {
      if (!sessionIsActive()) return;
      setMessages((current) => ({ ...current, [message.artistId]: mergeMessages(current[message.artistId] ?? [], [message]) }));
      const active = activeConversationRef.current;
      const isReading = active?.artist.id === message.artistId && document.visibilityState === "visible";
      setConversations((current) => {
        const exists = current.some((item) => item.artist.id === message.artistId);
        if (!exists) { void refreshConversations(); return current; }
        return current.map((item) => item.artist.id === message.artistId
          ? { ...item, latestMessage: message, unreadCount: isReading ? item.unreadCount : item.unreadCount + 1 }
          : item);
      });
      if (isReading) {
        void readMessages([message.id]).then(() => {
          if (!sessionIsActive()) return;
          setConversations((current) => current.map((item) => item.artist.id === message.artistId ? { ...item, unreadCount: 0 } : item));
        }).catch(() => {
          if (!sessionIsActive()) return;
          setConversations((current) => current.map((item) => item.artist.id === message.artistId
            ? { ...item, unreadCount: Math.max(1, item.unreadCount) }
            : item));
        });
      }
      else {
        notify(`${message.artistName} 发来新消息`, message.content);
        if (notificationEnabled && document.visibilityState !== "visible") {
          void navigator.serviceWorker?.ready.then((registration) => registration.showNotification(`${message.artistName} · KIRA`, { body: message.content, icon: message.artistAvatarUrl || "/kira-192.png", tag: `artist-${message.artistId}`, data: { url: `/?artistId=${encodeURIComponent(message.artistId)}` } }));
        }
      }
    });
    const offRetracted = realtime.onMessageRetracted((message) => {
      if (!sessionIsActive()) return;
      setMessages((current) => {
        const channel = current[message.artistId] ?? [];
        return {
          ...current,
          [message.artistId]: channel.map((item) => item.id === message.id ? { ...item, ...message } : item),
        };
      });
      setConversations((current) => current.map((item) => {
        if (item.latestMessage?.id !== message.id) return item;
        return {
          ...item,
          latestMessage: { ...item.latestMessage, ...message },
          unreadCount: Math.max(0, item.unreadCount - 1),
        };
      }));
      const active = activeConversationRef.current;
      if (active?.artist.id !== message.artistId) notify(`${message.artistName} 撤回了一条消息`);
    });
    const offOrders = realtime.onOrdersUpdated(() => {
      void Promise.all([getOrders(), getSellerOrders()]).then(([buyer, seller]) => {
        if (!sessionIsActive()) return;
        setOrders(buyer); setSellerOrders(seller);
      }).catch(() => { if (sessionIsActive()) notify("订单同步失败", "请刷新订单页面重试", "warn"); });
    });
    const offModeration = realtime.onModerationUpdated(() => {
      if (!sessionIsActive()) return;
      void refreshSystemNotifications();
    });
    const onVisible = () => { if (document.visibilityState === "visible") scheduleEntitlementsSync(); };
    document.addEventListener("visibilitychange", onVisible);
    realtime.connect();
    return () => { refreshVersion += 1; if (syncTimer) clearTimeout(syncTimer); document.removeEventListener("visibilitychange", onVisible); offOrders(); offSubscription(); offModeration(); offRetracted(); offMessage(); offStatus(); realtime.disconnect(); };
  }, [isCurrentSession, notificationEnabled, notify, refreshConversations, refreshFanMessageHistory, refreshSystemNotifications, user]);

  useEffect(() => {
    const version = ++feedLoadVersionRef.current;
    let cancelled = false;
    if (phase === "ready" && user && tab === "circles") {
      const epoch = sessionEpochRef.current;
      const userId = user.id;
      setCircleLoading(true);
      void Promise.all([getCircles({ includeHidden: "true" }), getPosts(circleFilter ?? undefined, postSort)]).then(([nextCircles, nextPosts]) => {
        if (cancelled || version !== feedLoadVersionRef.current || !isCurrentSession(epoch, userId)) return;
        setCircles(nextCircles); setPosts(nextPosts);
      }).catch((error) => {
        if (!cancelled && version === feedLoadVersionRef.current && isCurrentSession(epoch, userId)) notify("圈子暂时没有完全加载", error instanceof Error ? error.message : undefined, "warn");
      }).finally(() => { if (!cancelled && version === feedLoadVersionRef.current && isCurrentSession(epoch, userId)) setCircleLoading(false); });
    }
    return () => { cancelled = true; };
  }, [circleFilter, isCurrentSession, notify, phase, postSort, tab, user]);

  useEffect(()=>{
    if(!user)return;const epoch=sessionEpochRef.current;const userId=user.id;
    const refresh=()=>{void getProducts().then(rows=>{if(isCurrentSession(epoch,userId))setProducts(rows)}).catch(()=>undefined)};
    window.addEventListener('kira:market-updated',refresh);
    return()=>window.removeEventListener('kira:market-updated',refresh);
  },[user,isCurrentSession]);

  useEffect(() => {
    if (phase === "ready" && user && tab === "market" && !marketLoading && products.length === 0) {
      const epoch = sessionEpochRef.current;
      const feedVersion = feedLoadVersionRef.current;
      const userId = user.id;
      setMarketLoading(true);
      void Promise.all([
        getProducts(),
        getCart(),
        getOrders(),
        getSellerOrders(),
        circles.length ? Promise.resolve(circles) : getCircles({ includeHidden: "true" }),
        posts.length ? Promise.resolve(posts) : getPosts(),
        profile ? Promise.resolve(profile) : getProfile(),
      ]).then(([nextProducts, nextCart, nextOrders, nextSellerOrders, nextCircles, nextPosts, nextProfile]) => {
        if (!isCurrentSession(epoch, userId)) return;
        setProducts(nextProducts);
        setCart(nextCart);
        setOrders(nextOrders);
        setSellerOrders(nextSellerOrders);
        setCircles(nextCircles);
        if (feedVersion === feedLoadVersionRef.current) setPosts(nextPosts);
        setProfile(nextProfile);
      }).catch((error) => {
        if (isCurrentSession(epoch, userId)) notify("市集正在重连", error instanceof Error ? error.message : undefined, "warn");
      }).finally(() => { if (isCurrentSession(epoch, userId)) setMarketLoading(false); });
    }
  }, [circles, isCurrentSession, marketLoading, notify, phase, posts, products.length, profile, tab, user]);

  useEffect(() => {
    if (phase === "ready" && user && tab === "profile" && !profile) {
      const epoch = sessionEpochRef.current;
      const userId = user.id;
      void getProfile().then((nextProfile) => {
        if (isCurrentSession(epoch, userId)) setProfile(nextProfile);
      }).catch((error) => {
        if (isCurrentSession(epoch, userId)) notify("个人主页暂时无法更新", error instanceof Error ? error.message : undefined, "warn");
      });
    }
  }, [isCurrentSession, notify, phase, profile, tab, user]);

  useEffect(() => {
    const params = new URLSearchParams();
    const plan = new URLSearchParams(window.location.search).get("plan");
    if (plan) params.set("plan", plan);
    for (const key of ["post", "circle"]) { const value = new URLSearchParams(location.search).get(key); if (value) params.set(key,value); }
    if (tab === "home" && homeMode === "rendez") params.set("tab", "rendez");
    else if (tab !== "home") params.set("tab", tab);
    history.replaceState(null, "", `${location.pathname}${params.size ? `?${params}` : ""}`);
  }, [homeMode, tab]);

  useEffect(() => {
    if (!route.artistId || !conversations.length || activeConversation) return;
    const target = conversations.find((item) => item.artist.id === route.artistId);
    if (target) void openChat(target);
  }, [conversations]);

  async function openChat(conversation: Conversation) {
    const epoch = sessionEpochRef.current;
    const userId = activeUserIdRef.current;
    if (!isCurrentSession(epoch, userId)) return;
    setActiveConversation(conversation); setMessageLoading(true);
    const artistId = conversation.artist.id;
    try {
      const [broadcastResult, fanHistoryResult] = await Promise.allSettled([
        getMessages(artistId),
        refreshFanMessageHistory(artistId),
      ]);
      if (!isCurrentSession(epoch, userId)) return;
      let readConfirmed = true;
      if (broadcastResult.status === "fulfilled") {
        const next = broadcastResult.value;
        setMessages((current) => ({ ...current, [artistId]: mergeMessages(current[artistId] ?? [], next) }));
        const unreadIds = next.filter((message) => message.status === "published" && !message.readAt).map((message) => message.id);
        if (unreadIds.length) {
          try { await readMessages(unreadIds.slice(-100)); }
          catch {
            readConfirmed = false;
            if (isCurrentSession(epoch, userId)) notify("已读状态暂未同步", "网络恢复后会再次校对", "warn");
          }
        }
      }
      if (!isCurrentSession(epoch, userId)) return;
      if (readConfirmed) setConversations((current) => current.map((item) => item.artist.id === conversation.artist.id ? { ...item, unreadCount: 0 } : item));
      if (broadcastResult.status === "rejected" && fanHistoryResult.status === "rejected") {
        throw broadcastResult.reason;
      }
      if (broadcastResult.status === "rejected" || fanHistoryResult.status === "rejected") {
        const error = broadcastResult.status === "rejected" ? broadcastResult.reason : fanHistoryResult.status === "rejected" ? fanHistoryResult.reason : null;
        notify("部分聊天记录正在重连", error instanceof Error ? error.message : undefined, "warn");
      }
    } catch (error) {
      if (isCurrentSession(epoch, userId)) notify("更早的聊天记录没有加载", error instanceof Error ? error.message : undefined, "warn");
    }
    finally { if (isCurrentSession(epoch, userId)) setMessageLoading(false); }
  }

  async function loadOlderFanMessages(artistId: string) {
    const epoch = sessionEpochRef.current;
    const userId = activeUserIdRef.current;
    const currentPage = fanMessageHistory[artistId];
    if (!isCurrentSession(epoch, userId) || !currentPage?.hasMore || !currentPage.nextCursor || fanHistoryLoadingOlder === artistId) return;
    setFanHistoryLoadingOlder(artistId);
    try {
      const page = await getFanMessageHistory(artistId, { before: currentPage.nextCursor, limit: 50 });
      if (!isCurrentSession(epoch, userId)) return;
      setFanMessageHistory((current) => ({
        ...current,
        [artistId]: {
          items: mergeFanMessages(current[artistId]?.items ?? [], page.items),
          nextCursor: page.nextCursor,
          hasMore: page.hasMore,
        },
      }));
    } catch (error) {
      if (isCurrentSession(epoch, userId)) notify("更早的留言暂时没有取回", error instanceof Error ? error.message : undefined, "warn");
    } finally {
      if (isCurrentSession(epoch, userId)) setFanHistoryLoadingOlder(null);
    }
  }

  async function sendFanNote(artistId: string, content: string, operationKey: string): Promise<FanToArtistMessage> {
    const epoch = sessionEpochRef.current;
    const userId = activeUserIdRef.current;
    if (!userId || !isCurrentSession(epoch, userId)) throw new SessionChangedError();
    const message = await leaveArtistMessage(artistId, content, operationKey);
    if (!isCurrentSession(epoch, userId) || message.fanId !== userId) throw new SessionChangedError();
    setFanMessageHistory((current) => ({
      ...current,
      [artistId]: {
        items: mergeFanMessages(current[artistId]?.items ?? [], [message]),
        nextCursor: current[artistId]?.nextCursor ?? null,
        hasMore: current[artistId]?.hasMore ?? false,
      },
    }));
    setConversations((current) => sortConversationsByActivity(current.map((conversation) =>
      conversation.artist.id === artistId ? { ...conversation, latestFanMessage: message } : conversation,
    )));
    return message;
  }

  async function changeArtistBlock(artist: Artist, action: "block" | "unblock") {
    const epoch = sessionEpochRef.current;
    const userId = activeUserIdRef.current;
    if (!userId) return;
    setSubscriptionBusy(artist.id);
    try {
      await mutateSubscription(artist.id, action);
      if (!isCurrentSession(epoch, userId)) return;
      setActiveConversation(null); setArtistAccess(null);
      setMessages(current => { const next = { ...current }; delete next[artist.id]; return next; });
      setFanMessageHistory(current => { const next = { ...current }; delete next[artist.id]; return next; });
      const [nextHome, nextConversations, nextCircles, nextProfile, nextPosts] = await Promise.all([getFanHome(), getConversations(), getCircles({ includeHidden: "true" }), getProfile(), getPosts()]);
      if (!isCurrentSession(epoch, userId)) return;
      setHome(nextHome); setConversations(nextConversations); setCircles(nextCircles); setProfile(nextProfile); setPosts(nextPosts); setCircleFilter(null);
      notify(action === "block" ? "已拉黑艺人" : "已解除拉黑", action === "block" ? "相关内容已屏蔽，订阅有效期照常计算" : "尚未到期的订阅权益已恢复", "good");
    } catch (error) { if (isCurrentSession(epoch, userId)) notify("拉黑状态更新失败", error instanceof Error ? error.message : undefined, "warn"); throw error; }
    finally { if (isCurrentSession(epoch, userId)) setSubscriptionBusy(null); }
  }

  async function toggleSubscription(artist: Artist) {
    if (!artist.isSubscribed) { openArtistAccess(artist); return; }
    setSubscriptionBusy(artist.id);
    try {
      const updated = await mutateSubscription(artist.id, "unsubscribe");
      setHome((current) => current ? {
        ...current,
        artists: current.artists.map((item) => item.id === artist.id ? { ...item, ...updated } : item),
        featuredArtists: current.featuredArtists.map((item) => item.id === artist.id ? { ...item, ...updated } : item),
        newArtists: current.newArtists.map((item) => item.id === artist.id ? { ...item, ...updated } : item),
        subscriptions: updated.isSubscribed
          ? [...current.subscriptions.filter((item) => item.id !== updated.id), updated]
          : current.subscriptions.filter((item) => item.id !== updated.id),
      } : current);
      await refreshConversations();
      notify(updated.isSubscribed ? `已订阅 ${artist.stageName}` : `已取消订阅 ${artist.stageName}`, updated.isSubscribed ? "下一条消息会及时到达" : "聊天、官方圈成员与发帖权限已同步更新", "good");
    } catch (error) { notify("订阅状态没有更新", error instanceof Error ? error.message : undefined, "warn"); }
    finally { setSubscriptionBusy(null); }
  }

  function openArtistAccess(artist: Artist) {
    setArtistAccess(artist);
    if (!subscriptionPlans.length) {
      void getSubscriptionPlans().then(setSubscriptionPlans).catch((error) => {
        notify("订阅套餐暂时没有加载", error instanceof Error ? error.message : "请稍后重试", "warn");
      });
    }
  }

  async function purchaseArtistAccess(artistIds: string[], operationKey: string): Promise<SubscriptionOrder> {
    const epoch = sessionEpochRef.current;
    const userId = activeUserIdRef.current;
    setSubscriptionBusy(artistAccess?.id ?? artistIds[0] ?? "subscription");
    try {
      const order = await createSubscriptionOrder(artistIds, operationKey);
      const [nextHome, nextConversations, nextCircles, nextProfile] = await Promise.all([
        getFanHome(), getConversations(), getCircles({ includeHidden: "true" }), getProfile(),
      ]);
      if (!userId || !isCurrentSession(epoch, userId)) throw new SessionChangedError();
      setHome(nextHome);
      setConversations(nextConversations);
      setCircles(nextCircles);
      setProfile(nextProfile);
      setArtistAccess(null);
      notify("订阅已开通", order.items.map(item => `${item.artistName} 至 ${new Date(item.expiresAt).toLocaleDateString("zh-CN")}`).join("；"), "good");
      return order;
    } finally {
      setSubscriptionBusy(null);
    }
  }

  async function unsubscribeFromArtist(artist: Artist) {
    await toggleSubscription(artist);
    const [nextCircles, nextProfile] = await Promise.all([getCircles({ includeHidden: "true" }), getProfile()]);
    setCircles(nextCircles);
    setProfile(nextProfile);
    setArtistAccess(null);
  }

  async function changeCircleMembership(circle: Circle, action: "follow" | "unfollow") {
    if (circle.kind === "official" && action === "follow") {
      const artist = home?.artists.find(item => item.id === circle.artistId);
      if (artist && !artist.isSubscribed) { openArtistAccess(artist); return; }
    }
    setCircleMembershipBusy(circle.id);
    try {
      const updated = await mutateCircleMembership(circle.id, action);
      setCircles((current) => current.map((item) => item.id === updated.id ? updated : item));
      if (action === "unfollow" && circleFilter === circle.id) setCircleFilter(null);
      notify(action === "follow" ? `已关注 ${circle.name}` : `已退出 ${circle.name}`, action === "follow" ? "现在可以进入圈子发布动态" : "已退出圈子，公开内容仍可浏览；已有付费订阅不受影响", "good");
    } catch (error) {
      notify("圈子状态没有更新", error instanceof Error ? error.message : "请稍后再试", "warn");
    } finally {
      setCircleMembershipBusy(null);
    }
  }

  async function changePostInteraction(post: Post, action: "like" | "unlike" | "save" | "unsave") {
    try {
      const result = await mutatePostInteraction(post.id, action);
      const patchPost = (item: Post): Post => item.id === result.postId ? { ...item, isLiked: result.isLiked, isSaved: result.isSaved, likeCount: result.likeCount } : item;
      setPosts((current) => current.map(patchPost));
      setProfile((current) => current ? { ...current, posts: current.posts.map(patchPost), likedPosts: current.likedPosts.map(patchPost), savedPosts: current.savedPosts.map(patchPost) } : current);
      void getProfile().then(setProfile).catch(() => undefined);
      notify(action === "like" ? "已点亮喜欢" : action === "save" ? "已收藏到个人主页" : action === "unlike" ? "已取消点赞" : "已取消收藏", undefined, "good");
    } catch (error) {
      notify("互动没有完成", error instanceof Error ? error.message : "请稍后重试", "warn");
    }
  }

  function openCampaign(post: Post) {
    setCampaignId(post.id);
    const url = new URL(window.location.href); url.searchParams.set("plan", post.id); window.history.replaceState({}, "", url);
  }
  function closeCampaign() {
    setCampaignId(null);
    const url = new URL(window.location.href); url.searchParams.delete("plan"); window.history.replaceState({}, "", url);
  }
  useEffect(() => {
    if (phase !== "ready" || !user) return;
    const plan = new URLSearchParams(window.location.search).get("plan");
    if (plan) { setTab("circles"); setCampaignId(plan); }
  }, [phase, user?.id]);

  async function submitCampaignDraft(post: Post) {
    let operationKey = campaignPublishKeysRef.current.get(post.id);
    if (!operationKey) {
      operationKey = newOperationKey(`campaign-publish:${post.id}`);
      campaignPublishKeysRef.current.set(post.id, operationKey);
    }
    try {
      const updated = await publishCampaignDraft(post.id, operationKey);
      campaignPublishKeysRef.current.delete(post.id);
      const patchPost = (item: Post): Post => item.id === updated.id ? updated : item;
      setPosts((current) => current.map(patchPost));
      setProfile((current) => current ? { ...current, posts: current.posts.map(patchPost), likedPosts: current.likedPosts.map(patchPost), savedPosts: current.savedPosts.map(patchPost) } : current);
      void refreshSystemNotifications();
      notify("企划已提交审核", "运营处理结果会通过系统消息同步", "good");
    } catch (error) {
      notify("草稿没有提交", error instanceof Error ? error.message : "请稍后重试", "warn");
    }
  }

  async function saveProfile(input: { displayName: string; bio: string }) {
    const epoch = sessionEpochRef.current;
    const userId = activeUserIdRef.current;
    if (!userId || !isCurrentSession(epoch, userId)) throw new SessionChangedError();
    const updated = await updateFanProfile(input);
    if (!isCurrentSession(epoch, userId) || updated.id !== userId) throw new SessionChangedError();
    cacheCurrentUser(updated, userId);
    activateSession(updated);
    setProfile((current) => current ? { ...current, user: updated } : current);
    setProfileEditOpen(false);
    notify("个人资料已更新", "昵称与简介已经同步到当前账号", "good");
  }

  async function refreshPosts() {
    if (!user) return;
    const epoch = sessionEpochRef.current, userId = user.id;
    const version = ++feedLoadVersionRef.current;
    const current = () => version === feedLoadVersionRef.current && isCurrentSession(epoch, userId);
    setCircleLoading(true);
    try { const next = await getPosts(circleFilter ?? undefined, postSort); if (current()) setPosts(next); }
    catch (error) { if (current()) notify("信息流刷新失败", error instanceof Error ? error.message : undefined, "warn"); }
    finally { if (current()) setCircleLoading(false); }
  }

  async function addToCart(product: Product) {
    try { const next = await updateCart(product.id, (cart.find((item) => item.id === product.id)?.quantity ?? 0) + 1); setCart(next); notify("已放入购物车", product.title, "good"); }
    catch (error) { notify("购物车没有更新", error instanceof Error ? error.message : undefined, "warn"); }
  }

  async function setCartSelection(item: CartItem, selected: boolean) {
    try { setCart(await updateCart(item.id, item.quantity, selected)); }
    catch { notify("选择状态没有更新", "请稍后再试", "warn"); }
  }

  async function setCartQuantity(item: CartItem, quantity: number) {
    try { setCart(await updateCart(item.id, quantity, item.selected)); }
    catch (error) { notify("购物车数量没有更新", error instanceof Error ? error.message : "请稍后再试", "warn"); }
  }

  async function setAllCartSelection(items: CartItem[], selected: boolean) {
    try {
      for (const item of items) await updateCart(item.id, item.quantity, selected);
      setCart(await getCart());
    } catch (error) {
      setCart(await getCart().catch(() => cart));
      notify("全选状态没有完全更新", error instanceof Error ? error.message : "请稍后再试", "warn");
    }
  }

  async function submitCheckout() {
    const ids = cart.filter((item) => item.selected !== false).map((item) => item.id);
    if (!ids.length) { notify("先选择一件好物", undefined, "warn"); return; }
    try {
      const fingerprint = JSON.stringify([...ids].sort());
      if (!checkoutOperationRef.current || checkoutOperationRef.current.fingerprint !== fingerprint) {
        checkoutOperationRef.current = { fingerprint, key: newOperationKey("checkout") };
      }
      const createdOrders = await checkout(ids, checkoutOperationRef.current.key);
      checkoutOperationRef.current = null;
      setOrders((current) => [...createdOrders, ...current]);
      setCart(await getCart());
      notify("订单已创建", `已按卖家拆分为 ${createdOrders.length} 笔订单，可在“我的订单”查看`, "good");
    }
    catch (error) { notify("订单没有提交", error instanceof Error ? error.message : undefined, "warn"); }
  }

  async function publishMarketProduct(input: CreateProductInput, operationKey: string) {
    const created = await createProduct(input, operationKey);
    setProducts((current) => [created, ...current.filter((item) => item.id !== created.id)]);
    window.dispatchEvent(new Event("kira:market-updated"));
    notify(input.isCarpool ? "发车成功" : "出物成功", "商品已经出现在同担市集", "good");
  }

  async function shipMarketOrder(orderId: string, input: ShipOrderInput, operationKey: string) {
    const shipped = await shipSellerOrder(orderId, input, operationKey);
    setSellerOrders((current) => current.map((item) => item.id === shipped.id ? shipped : item));
    notify("发货信息已保存", `${shipped.carrier ?? "承运方"} · ${shipped.trackingNumber ?? "运单号"}`, "good");
  }

  async function createWish(circleId: string, content: string, operationKey: string) {
    const post = await createPost({ circleId, type: "normal", content: `#需求墙 ${content}`, mediaUrls: [], publishMode: "publish" }, operationKey);
    setPosts((current) => [post, ...current]);
  }

  function toggleMarketProductFollow(product: Product, followed: boolean) {
    setFollowedMarketProductIds((current) => {
      const next = followed ? [...new Set([...current, product.id])] : current.filter((id) => id !== product.id);
      localStorage.setItem(`kira:market:${user?.id}:followed-products`, JSON.stringify(next));
      return next;
    });
  }

  function toggleMarketSellerFollow(seller: MarketV2SellerProfile, followed: boolean) {
    setFollowedMarketSellerIds((current) => {
      const next = followed ? [...new Set([...current, seller.id])] : current.filter((id) => id !== seller.id);
      localStorage.setItem(`kira:market:${user?.id}:followed-sellers`, JSON.stringify(next));
      return next;
    });
  }

  function handleAccountAction(action: AccountHubAction) {
    setDrawer(false);
    if (action === "logout") { void doLogout(); return; }
    if (action === "subscriptions") {
      setArtistDirectoryFilter("subscribed");
      setArtistDirectoryOpen(true);
      return;
    }
    if (action === "orders") { navigate("market"); setMarketEntry("buyer-orders"); return; }
    if (action === "about") { setAccountPanel("about"); return; }
    setAccountPanel(action);
  }

  function openSystemInbox() {
    setDrawer(false);
    setSystemInboxOpen(true);
    void refreshSystemNotifications();
  }

  async function markSystemNotificationsRead(notificationIds: string[]) {
    const uniqueIds = [...new Set(notificationIds)];
    if (!uniqueIds.length) return;
    try {
      const result = await readNotifications(uniqueIds);
      const readIds = new Set(result.notificationIds);
      setSystemNotifications((current) => current.map((item) => readIds.has(item.id)
        ? { ...item, status: "read" }
        : item));
    } catch {
      notify("已读状态没有同步", "请检查网络后再试", "warn");
    }
  }

  async function enableNotifications() {
    if (notificationBusy) return;
    if (import.meta.env.DEV) { notify("当前环境不支持锁屏通知", "请使用生产预览或已部署版本", "warn"); return; }
    if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) { notify("此浏览器不支持推送", "新消息仍会在应用内显示", "warn"); return; }
    setNotificationBusy(true);
    try {
      const config = await getPushConfig();
      const publicKey = config.publicKey || config.vapidPublicKey;
      if (!notificationEnabled && !publicKey) throw new Error("推送服务尚未配置");
      const existingRegistration = await navigator.serviceWorker.getRegistration();
      if (!existingRegistration) throw new Error("通知组件尚未就绪，请重新打开应用后重试");
      const registration = await new Promise<ServiceWorkerRegistration>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error("通知组件准备超时，请重新打开应用后重试")), 5000);
        navigator.serviceWorker.ready.then(value => { clearTimeout(timer); resolve(value); }, error => { clearTimeout(timer); reject(error); });
      });
      if (notificationEnabled) {
        const existing = await registration.pushManager.getSubscription();
        if (existing) { await removePushSubscription(existing.endpoint); await existing.unsubscribe(); }
        setNotificationEnabled(false); notify("锁屏提醒已关闭", "应用内消息不受影响", "good"); return;
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") { notify("通知权限没有开启", "你可以稍后在浏览器设置中调整", "warn"); return; }
      if (!publicKey) throw new Error("推送服务尚未配置");
      const subscription = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) });
      await savePushSubscription(subscription.toJSON());
      setNotificationEnabled(true); notify("新消息通知已开启", "新消息可在锁屏界面提醒你", "good");
    } catch (error) { notify("通知设置未完成", error instanceof Error ? error.message : undefined, "warn"); }
    finally { setNotificationBusy(false); }
  }

  async function installApp() {
    if (!installPrompt) { setInstallGuide(true); return; }
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === "accepted") setInstallGuide(false);
    setInstallPrompt(null);
  }

  async function doLogout() {
    setLoginBusy(true);
    setLoginError("");
    invalidateSession();
    setPhase("logged-out");
    try { await logout(); } catch { /* local session still closes */ }
    finally { setLoginBusy(false); }
  }

  async function doRegister(input: FanRegistrationInput) {
    setLoginBusy(true);
    setLoginError("");
    try {
      await registerFan(input);
      await bootstrap();
    }
    catch (error) {
      reportClientError(error, { phase: "fan-registration" });
      setLoginError(error instanceof Error ? error.message : "登录没有完成，请稍后再试");
      setPhase("logged-out");
    }
    finally { setLoginBusy(false); }
  }

  function navigate(next: MainTab) { setMarketEntry("home"); setTab(next); if (next !== "home") setActiveConversation(null); window.scrollTo({ top: 0, behavior: "smooth" }); }

  if (phase === "booting") return <LoadingScreen />;
  if (phase === "logged-out") return <><LoginGate onRegister={doRegister} busy={loginBusy} error={loginError} /><Toasts messages={toasts} /></>;
  if (phase === "error" || !user) return <main className="fatal"><span className="fatal__code">暂时无法连接</span><h1>KIRA 没有打开</h1><p>{fatalMessage || "请检查网络后重试"}</p><Button className="k-button--accent" onClick={() => void bootstrap()}><RefreshCw size={18} />重新加载</Button></main>;

  const unread = conversations.reduce((sum, item) => sum + item.unreadCount, 0);
  const seenRendezArtists = new Set(seenRendezArtistIds);
  const newEncounterCount = rendezSeenReady ? (home?.subscriptions ?? []).filter((artist) => !seenRendezArtists.has(artist.id)).length : 0;
  const systemUnread = systemNotifications.reduce((sum, item) => sum + (item.status === "unread" ? 1 : 0), 0);
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as Window & { MSStream?: unknown }).MSStream;
  const marketSellerProfiles = [...products.reduce((profiles, product) => {
    const current = profiles.get(product.sellerId);
    profiles.set(product.sellerId, {
      id: product.sellerId,
      displayName: product.sellerName,
      completedOrders: (current?.completedOrders ?? 0) + product.sales,
      followerCount: followedMarketSellerIds.includes(product.sellerId) ? 1 : 0,
      isFollowed: followedMarketSellerIds.includes(product.sellerId),
    });
    return profiles;
  }, new Map<string, MarketV2SellerProfile>()).values()];
  const followedMarketProducts = products.filter((product) => followedMarketProductIds.includes(product.id));
  const followedMarketSellers = marketSellerProfiles.filter((seller) => followedMarketSellerIds.includes(seller.id));
  const arrangedMyCircles = circleArrangeItems.filter((item) => !item.hidden).map((item) => item.circle);
  const marketOrderViews = [...orders, ...sellerOrders].map((order) => ({
    order,
    ...(order.trackingNumber ? {
      trackingEvents: [{
        id: `tracking-${order.id}`,
        content: `${order.carrier ?? "承运方"} · ${order.trackingNumber}`,
        occurredAt: order.shippedAt ?? order.createdAt,
      }],
    } : {}),
  }));

  return (
    <div className={`app-shell kira-ambient app-shell--${tab}`}>
      <MainNav tab={tab} onChange={navigate} />
      <div className="app-stage">
        <TopBar tab={tab} user={user} connected={connected} broadcastUnread={unread} systemUnread={systemUnread} onOpenRendez={() => { setTab("home"); setHomeMode("rendez"); }} onOpenSystemInbox={openSystemInbox} onOpenDrawer={() => setDrawer(true)} />
        <main className="app-content">
          {tab !== "market" ? <ScopedSearch key={`${user.id}:${tab}`} scope={tab} onAccount={section => { if(section === "following") setAccountPanel("following"); else if(section === "saved") setProfileContentTab("saved"); else { navigate("market"); setMarketEntry("buyer-orders"); } }} onArtist={openArtistAccess} onCircle={circle => { setCircleFilter(circle.id); navigate("circles"); }} onOrder={order => setOrderAction({order, action:"details", userId:user.id})} onMessage={artistId => { const conversation=conversations.find(c=>c.artist.id===artistId); if(conversation) void openChat(conversation); }} /> : null}
          {tab === "home" ? <HomePage home={home} conversations={conversations} mode={homeMode} rankRange={rankRange} newEncounterCount={newEncounterCount} onMode={setHomeMode} onRange={setRankRange} onOpenArtist={openArtistAccess} subscriptionBusy={subscriptionBusy} onChat={(conversation) => void openChat(conversation)} onExploreCircle={(circle) => { setCircleFilter(circle.id); navigate("circles"); }} onOpenArtists={() => setArtistDirectoryOpen(true)} onOpenCircles={() => setCircleDirectoryOpen(true)} /> : null}
          {tab === "circles" ? <CirclesPage circles={circles} arrangedMyCircles={arrangedMyCircles} posts={posts} selectedCircle={circleFilter} sort={postSort} loading={circleLoading} membershipBusy={circleMembershipBusy} currentUserId={user.id} onSelectCircle={setCircleFilter} onSort={setPostSort} onRefresh={() => void refreshPosts()} onOpenPost={() => setPostComposer(true)} onOpenCircle={() => setCircleComposer(true)} onArrange={() => setCircleArrangeOpen(true)} onMembership={(circle, action) => void changeCircleMembership(circle, action)} onPostInteraction={(post, action) => void changePostInteraction(post, action)} onParticipation={post => openCampaign(post)} onPublishDraft={(post) => void submitCampaignDraft(post)} /> : null}
          {tab === "market" ? <MarketV2
            key={`${user.id}:${marketEntry}`}
            initialPanel={marketEntry}
            searchStorageKey={`kira-market-searches:${user.id}`}
            currentUser={user}
            artists={home?.artists ?? []}
            products={products}
            cart={cart}
            buyerOrders={orders}
            sellerOrders={sellerOrders}
            followedProducts={followedMarketProducts}
            followedSellers={followedMarketSellers}
            sellerProfiles={marketSellerProfiles}
            orderViews={marketOrderViews}
            loading={marketLoading}
            onOpenProduct={(product) => setMarketProductDetail(product)}
            onAddToCart={(product) => addToCart(product)}
            onJoinCarpool={(product) => addToCart(product)}
            onToggleProductFollow={toggleMarketProductFollow}
            onToggleSellerFollow={toggleMarketSellerFollow}
            onToggleCartItem={(productId, selected) => {
              const item = cart.find((candidate) => candidate.id === productId);
              if (item) return setCartSelection(item, selected);
              return undefined;
            }}
            onSelectSellerCart={(sellerId, selected) => setAllCartSelection(cart.filter((item) => item.sellerId === sellerId), selected)}
            onChangeCartQuantity={(productId, quantity) => {
              const item = cart.find((candidate) => candidate.id === productId);
              if (item) return setCartQuantity(item, quantity);
              return undefined;
            }}
            onCheckout={() => submitCheckout()}
            onStartProductPublish={(type) => { setMarketProductType(type); setMarketComposerKind("item"); }}
            onStartCarpoolPublish={() => setMarketComposerKind("carpool")}
            onBuyerOrderAction={(order, action) => setOrderAction({ order, action: action === "confirm" || action === "refund" || action === "review" ? action : "details", userId: user.id })}
            onSellerOrderAction={(order, action) => {
              if (action === "ship") { setMarketShippingOrder(order); return; }
              if (action === "view-logistics" && order.trackingNumber) {
                notify("物流信息", `${order.carrier ?? "承运方"} · ${order.trackingNumber}`);
                return;
              }
              setOrderAction({ order, action: action === "handle-refund" ? "handle-refund" : "details", userId: user.id });
            }}
          /> : null}
          {tab === "profile" ? <ProfilePage profile={profile} posts={posts} contentTab={profileContentTab} onContentTab={setProfileContentTab} onEditProfile={() => setProfileEditOpen(true)} onOpenDrawer={() => setDrawer(true)} onExploreCircles={() => navigate("circles")} onPostInteraction={(post, action) => void changePostInteraction(post, action)} onParticipation={post => openCampaign(post)} onPublishDraft={(post) => void submitCampaignDraft(post)} /> : null}
        </main>
        <MainNav tab={tab} onChange={navigate} />
      </div>
      {activeConversation ? <ChatScreen
        conversation={activeConversation}
        messages={messages[activeConversation.artist.id] ?? []}
        fanMessages={fanMessageHistory[activeConversation.artist.id]?.items ?? []}
        user={user}
        connected={connected}
        loading={messageLoading}
        hasMoreFanMessages={fanMessageHistory[activeConversation.artist.id]?.hasMore ?? false}
        loadingOlderFanMessages={fanHistoryLoadingOlder === activeConversation.artist.id}
        onBlock={() => void changeArtistBlock(activeConversation.artist, "block").catch(() => undefined)}
        onClose={() => setActiveConversation(null)}
        onRetry={() => void openChat(activeConversation)}
        onLoadOlderFanMessages={() => loadOlderFanMessages(activeConversation.artist.id)}
        onLeaveMessage={(content, operationKey) => sendFanNote(activeConversation.artist.id, content, operationKey)}
      /> : null}
      <SharedPost />
      {tab === "circles" ? <div className="circle-compose-dock"><button type="button" onClick={() => setPostComposer(true)}><PenLine size={18} /><span>写下此刻，发到你的圈子…</span></button><button type="button" aria-label="发布动态" onClick={() => setPostComposer(true)}><Send size={18} /></button></div> : null}
      {postComposer ? <PostComposer user={user} circles={circles} defaultCircleId={circleFilter} onCreated={(post) => { if (!circleFilter || circleFilter === post.circleId) setPosts((current) => [post, ...current.filter((item) => item.id !== post.id)]); setProfile((current) => current ? { ...current, posts: [post, ...current.posts.filter((item) => item.id !== post.id)] } : current); if (post.status === "pending") void refreshSystemNotifications(); }} onClose={() => setPostComposer(false)} notify={notify} /> : null}
      {circleComposer ? <CircleComposer user={user} onCreated={(circle) => { setCircles((current) => [circle, ...current]); void refreshSystemNotifications(); }} onClose={() => setCircleComposer(false)} notify={notify} /> : null}
      {artistAccess ? <Modal title="订阅艺人" eyebrow="选择想订阅的艺人" onClose={() => setArtistAccess(null)} wide className="modal-sheet--artist-access"><SubscriptionAccess key={artistAccess.id} focusArtist={artistAccess} artists={home?.artists ?? []} plans={subscriptionPlans} busy={subscriptionBusy !== null} onPurchase={purchaseArtistAccess} onUnsubscribe={unsubscribeFromArtist} onBlock={() => void changeArtistBlock(artistAccess, "block").catch(() => undefined)} /></Modal> : null}
      {artistDirectoryOpen ? <Modal title="全部艺人" eyebrow="发现更多" onClose={() => setArtistDirectoryOpen(false)} wide><ArtistDirectory artists={home?.artists ?? []} filter={artistDirectoryFilter} onFilterChange={setArtistDirectoryFilter} onArtistOpen={(artist) => { setArtistDirectoryOpen(false); openArtistAccess(artist); }} /></Modal> : null}
      {circleDirectoryOpen ? <Modal title="全部圈子" eyebrow="发现更多" onClose={() => setCircleDirectoryOpen(false)} wide><CircleDirectory circles={circles.length ? circles : home?.circles ?? []} sort={circleDirectorySort} onSortChange={setCircleDirectorySort} onCircleOpen={(circle) => { setCircleDirectoryOpen(false); setCircleFilter(circle.id); navigate("circles"); }} onCommunityMembershipToggle={(circle) => void changeCircleMembership(circle, circle.isFollowed ? "unfollow" : "follow")} /></Modal> : null}
      <CircleArrangeSheet open={circleArrangeOpen} items={circleArrangeItems} onChange={(items) => setCircleArrangeItems(items)} onClose={() => setCircleArrangeOpen(false)} onDone={(items) => { const arrangement = { order: items.map((item) => item.circle.id), hidden: items.filter((item) => item.hidden).map((item) => item.circle.id) }; localStorage.setItem(`kira:circle-arrangement:${user.id}`, JSON.stringify(arrangement)); setCircleArrangeItems([...items]); setCircleArrangeOpen(false); void setCirclePreferences({ items: items.map((item, position) => ({ circleId: item.circle.id, position, hidden: item.hidden })) }).then(() => notify("圈子顺序已同步", "隐藏项保留原位置，并会跟随当前账号", "good")).catch((error) => notify("已保存在当前设备", error instanceof Error ? error.message : "账号同步暂未完成", "warn")); }} />
      {marketComposerKind ? <Modal title={marketComposerKind === "carpool" ? "我要发车" : "我要出物"} eyebrow="同担市集 · 安全发布" onClose={() => setMarketComposerKind(null)} wide><ProductComposer initialType={marketProductType} kind={marketComposerKind} artists={home?.artists ?? []} onSubmit={publishMarketProduct} onClose={() => setMarketComposerKind(null)} /></Modal> : null}
      {campaignId ? <Modal title="应援企划 · 参与" eyebrow="Join" className="modal-sheet--campaign-join" onClose={closeCampaign}><CampaignJoin key={`${user.id}:${campaignId}`} postId={campaignId} onUpdated={updated => {
        if (activeUserIdRef.current !== user.id) return;
        const patch = (post: Post) => post.id === updated.id ? updated : post;
        setPosts(items => items.map(patch));
        setProfile(current => current ? { ...current, posts: current.posts.map(patch), likedPosts: current.likedPosts.map(patch), savedPosts: current.savedPosts.map(patch) } : current);
      }} /></Modal> : null}
      {orderAction && orderAction.userId === user.id ? <Modal title={orderAction.action === "confirm" ? "确认收货" : orderAction.action === "refund" ? "申请退款" : orderAction.action === "review" ? "评价订单" : orderAction.action === "handle-refund" ? "处理退款" : "订单详情"} eyebrow="订单服务" onClose={() => setOrderAction(null)}><OrderActionPanel key={`${user.id}:${orderAction.order.id}:${orderAction.action}`} initialOrder={orderAction.order} action={orderAction.action} onUpdated={updated => { if (activeUserIdRef.current !== orderAction.userId) return; setOrders(current => current.map(order => order.id === updated.id ? updated : order)); setSellerOrders(current => current.map(order => order.id === updated.id ? updated : order)); }} /></Modal> : null}
      {marketShippingOrder ? <Modal title="填写发货信息" eyebrow="订单履约 · 可追踪" onClose={() => setMarketShippingOrder(null)}><div className="order-list seller-order-list"><SellerOrderCard order={marketShippingOrder} onShip={async (orderId, input, operationKey) => { await shipMarketOrder(orderId, input, operationKey); setMarketShippingOrder(null); }} notify={notify} /></div></Modal> : null}
      {marketProductDetail ? <Modal title="商品详情" eyebrow="同担市集 · 真实描述" onClose={() => setMarketProductDetail(null)}><div className="product-grid product-grid--detail"><ProductCard product={marketProductDetail} owned={marketProductDetail.sellerId === user.id} onAdd={(product) => { void addToCart(product); }} /></div></Modal> : null}
      {profileEditOpen ? <Modal title="编辑个人资料" eyebrow="个人主页" onClose={() => setProfileEditOpen(false)}><ProfileEditor user={user} onSave={saveProfile} /></Modal> : null}
      {installGuide ? <InstallGuide isIOS={isIOS} canPrompt={Boolean(installPrompt)} onInstall={() => void installApp()} onClose={() => setInstallGuide(false)} /> : null}
      {systemInboxOpen ? <SystemInbox notifications={systemNotifications} loading={systemInboxLoading} error={systemInboxError} onClose={() => setSystemInboxOpen(false)} onRetry={() => void refreshSystemNotifications()} onRead={(notificationIds) => void markSystemNotificationsRead(notificationIds)} onReadAll={() => void markSystemNotificationsRead(systemNotifications.filter((item) => item.status === "unread").map((item) => item.id))} /> : null}
      {drawer ? <div className="drawer-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setDrawer(false); }}><AccountHub open user={user} counts={{ subscriptions: profile?.stats.subscriptions ?? home?.artists.filter((artist) => artist.isSubscribed).length ?? 0, orders: orders.length, following: profile?.stats.following ?? 0, followers: profile?.stats.followers ?? 0 }} onClose={() => setDrawer(false)} onAction={handleAccountAction} /></div> : null}
      {accountPanel === "about" ? <Modal title="关于 KIRA" eyebrow="规则、联系与资质" onClose={() => setAccountPanel(null)}><AboutKira onAction={setAboutDetail} version="1.0.0 · PWA" /></Modal> : null}
      {accountPanel && accountPanel !== "about" ? <Modal title={accountPanel === "realname" ? "实名认证" : accountPanel === "phone" ? "手机号绑定" : accountPanel === "settings" ? "设置" : accountPanel === "help" ? "帮助与反馈" : accountPanel === "following" ? "我的关注" : "我的粉丝"} eyebrow="账号" onClose={() => setAccountPanel(null)}>
        <div className="account-feature-panel">
          {accountPanel === "realname" || accountPanel === "phone" ? <IdentityForm key={accountPanel} kind={accountPanel} /> : null}
          {accountPanel === "settings" ? <><BlockedArtists onChange={changeArtistBlock} /><button type="button" disabled={import.meta.env.DEV || notificationBusy} onClick={() => void enableNotifications()}><BellRing size={18} /><span><strong>{import.meta.env.DEV ? "当前环境不支持锁屏通知" : notificationBusy ? "正在设置通知…" : notificationEnabled ? "关闭新消息通知" : "开启新消息通知"}</strong><small>通知权限会跟随当前设备设置</small></span><ChevronRight size={17} /></button><button type="button" onClick={() => { setAccountPanel(null); setInstallGuide(true); }}><Download size={18} /><span><strong>{installed ? "KIRA 已添加到桌面" : "添加 KIRA 到桌面"}</strong><small>下次可以像普通 App 一样打开</small></span><ChevronRight size={17} /></button><button type="button" onClick={openSystemInbox}><Bell size={18} /><span><strong>系统消息</strong><small>{systemUnread ? `${systemUnread} 条审核进度待查看` : "审核进度已同步"}</small></span><ChevronRight size={17} /></button></> : null}
          {accountPanel === "following" ? <div className="feedback-list"><FollowedArtists /><p>这里展示你关注或创建的自建圈，艺人官方圈随订阅自动加入。</p>{(profile?.followedCircles ?? []).filter(circle => circle.kind === "community" && circle.isFollowed).length ? (profile?.followedCircles ?? []).filter(circle => circle.kind === "community" && circle.isFollowed).map(circle => <button type="button" key={circle.id} onClick={() => { setAccountPanel(null); setCircleFilter(circle.id); navigate("circles"); }}>{circle.name}<ChevronRight size={16}/></button>) : <EmptyState title="还没有关注圈子" body="在圈子页浏览并关注感兴趣的自建圈。" />}</div> : null}
          {accountPanel === "help" ? <><ReportHistory /><div className="account-feature-panel__safe"><MessageCircleMore size={20} /><span><strong>需要帮助？</strong>可以先查看系统消息中的处理进度，也可以从关于页找到联系信息。</span></div><button type="button" onClick={() => { setAccountPanel(null); setSystemInboxOpen(true); }}><BellRing size={18} /><span><strong>查看系统消息</strong><small>审核结果与平台通知</small></span><ChevronRight size={17} /></button></> : null}
        </div>
      </Modal> : null}
      {aboutDetail ? <Modal title={aboutDetail === "contact" ? "联系我们" : aboutDetail === "qualifications" ? "资质备案" : "规则说明"} eyebrow="关于 KIRA" onClose={() => setAboutDetail(null)}><div className="account-policy-note"><ShieldCheck size={22} /><h3>{aboutDetail === "contact" ? "联系与反馈" : aboutDetail === "qualifications" ? "平台信息" : "规则说明"}</h3><p>{aboutDetail === "contact" ? "如遇到账号、内容或交易问题，请通过帮助与反馈告诉我们。" : aboutDetail === "qualifications" ? "平台主体信息、许可与备案会统一展示在这里。" : "请遵守社区约定，尊重他人，也保护好自己的隐私和交易安全。"}</p></div></Modal> : null}
      <Toasts messages={toasts} />
    </div>
  );
}
